import { useState, useMemo, useEffect, useRef, Component } from 'react'
import { currentEncounterNumber, activeEncounter } from '../utils/caseEncounters'
console.log('[CaseHome] v184.0 loaded')
import { createPortal } from 'react-dom'
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, ChevronLeft, Check, Lock, FileText, Camera, Stethoscope, ClipboardList, Share2, AlertTriangle, AlertCircle, X, Trash2, Archive, Calendar, Clock, Edit3, UserCheck, Send, Eye, CheckCircle, FilePlus, FileCheck, Upload, RefreshCw, MessageCircle, FileSignature, Mail, Mic, FolderOpen } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../stores'
import { needsAreaPick } from '../utils/mdCaseArea' // s117 (D529)
import NotificationBellButton from '../components/NotificationBellButton'
import useQuestionnaireStore from '../stores/useQuestionnaireStore'
import { useI18n } from '../i18n'
import { resolveShareStatus, isCodeSharedUnclaimed, doctorWaitLabels, hasAttendingDoctor } from '../utils/caseShareState'
import { currentOfficeSend, officeLines } from '../utils/officeSend' // s99 D475: "Sent to" before the doctor accepts
import { useQuestionnaireTranslation } from '../i18n/questionnaires/useQuestionnaireTranslation'
import OrellaChatAgent from '../components/agent/OrellaChatAgent'
import { buildProfileSummary } from '../utils/profileSummary'
import { formatDate, formatAppointmentTime, formatCalendarDay } from '../utils/dateUtils'
import { bookableSlots, appointmentErrorKeyFor, appointmentRefused, slotPlace, slotPlaceNames } from '../utils/bookableSlots'
import SlotPlaceLine from '../components/SlotPlaceLine'
import { resolveActiveEncounter, resolveCarePlan } from '../hooks/useActiveCarePlan'
import { resolveStageBlurbs } from '../components/casehome/stageModel'
import AskOrellaChip from '../components/AskOrellaChip'
// v242.69.0 (TF #19/#20): the 4D spine as a horizontal pager. PRESENTATIONAL —
// it consumes the same `sections` array the timeline does and computes no state.
import StagePager, { activeStageIndex } from '../components/casehome/StagePager'
import { caseStatusModel } from '../components/casehome/caseStatus' // s124: the status card
import CaseStatusBlock from '../components/casehome/CaseStatusBlock' // s124
import { isCheckInEligible } from '../components/casehome/appointmentWindow'
import AppointmentActions from '../components/casehome/AppointmentActions'
import ConfirmModal from '../components/ConfirmModal'
import { withdrawCaseFromDoctor, withdrawDestination } from '../utils/withdrawCase'
import { readServerAppointmentRequest } from '../utils/appointmentRequestOwner'
import { canWithdrawCase } from '../utils/canWithdraw'
import CaseGroup, { GroupLine, Tag } from '../components/casehome/CaseGroup'
import RemindDoctorSheet from '../components/RemindDoctorSheet'
import CaseReportViewer from '../components/share/CaseReportViewer' // s101 P1.2
import ReferralRequestCard from '../components/casehome/ReferralRequestCard' // s106 referral screens A5/A6/A8
import VisitPrepDisclaimer from '../components/VisitPrepDisclaimer'
import { OverlayPortal } from '../components/ModalPortal'
import CustomDocHandler from '../components/CustomDocHandler'
import OrellaLoader, { OrellaSpinner } from '../components/OrellaLoader'
import { caseHomeView } from '../utils/caseHomeState'
import { formatSpecialtyName, formatCaseSpecialty } from '../utils/specialtyResolver'
import { deliverNeedsOrellaMD as deliverNeedsOrellaMDFn, deliverGatedSubscription as deliverGatedSubscriptionFn } from '../utils/caseGating'

// Import white logo for header
import orellaLogoWhite from '../assets/logos/orella_white_logo.png'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
import { isHeldByDoctor, reportDeleteFailure } from '../utils/caseDelete'
import { allowedVisitTypesForDoctor } from '../utils/offeredModalities' // PROD-644
import { caseDoctor } from '../utils/caseDoctor' // PROD-644
import { useBookingRecordingConsent } from '../components/RecordingConsentSheet' // MEET PA0
import { asPendingRequest, hasPendingReschedule, requestedChangeOf, PENDING_RESCHEDULE } from '../utils/pendingReschedule' // PROD-921, P20
import { isUuid } from '../utils/videoVisit' // PROD-925 Q1: a real row id is a uuid
import { isDeclined, requestDeclined, followUpDeclined, CANCELLED, requestCancelledByPhysician, followUpCancelledByPhysician, physicianCancelOfRequest, physicianCancelOfFollowUp, cancelReasonKey } from '../utils/declinedRequest' // PROD-983, PROD-1137
import { rowsOfCurrentPhysician, initialRowFor } from '../utils/currentPhysicianRows' // PROD-1020
import { agentVisitContext } from '../utils/signedVisit' // PROD-1079: the agent hears signed, not-held notes only
import { planFollowUp } from '../utils/signedPlan' // PROD-1079: no draft plan reaches the patient or the agent
import { pickHeaderVisit, visitParts } from '../utils/visitHeader' // s126: the header names the next booked, else the most recent visit
import { isOutsideAppointment } from '../utils/outsideAppointment' // PROD-1160: finders skip the patient's outside visit
import { hasUnseenSummaryUpdate } from '../utils/summaryFreshness' // s111 (TF b78): the Describe step's "Updated" pill
import { clinicianForms, clinicianFromText, clinicianName, withCaseClinician } from '../utils/clinicianName.js'
import { safePath } from '../utils/safePath' // PROD-1313: history-state returnTo is checked before navigate()
import { useOpenRequest } from '../hooks/useOpenRequest' // PROD-1395: "Allow access?" on the case itself

// ============ PHASE 1: NEW COLOR CONSTANTS ============
const NAVY = '#15354F'           // Header background
const ORELLA_BLUE = '#46A8F9'    // Detect color, buttons

// v206.1: SPECIALTY_KEY_MAP + formatSpecialtyName moved to shared utils/specialtyResolver.js

// Progress bar colors (4 D's)
const PROGRESS_COLORS = {
  describe: '#A7D9FC',  // Light blue
  detect: '#46A8F9',    // Medium blue  
  diagnose: '#15354F',  // Navy
  deliver: '#66D170',   // Green
}

// v143.4: Darker text colors for outlined badge readability
const BADGE_TEXT_COLORS = {
  describe: '#1E6DA0',  // Darker blue for light blue phase
  detect: '#1A6FC2',    // Darker blue
  diagnose: '#15354F',  // Navy
  deliver: '#1B873A',   // Darker green
}

// s124: the header's status pill (StatusBadge, STATUS_STYLES) is gone — CaseStatusBlock in the Orella card says
//   where the case stands and what happens next (canvas "Orella Case Home — Revised").

// ============ PROGRESS BAR COMPONENT ============
function ProgressBar({ describeComplete, detectComplete, diagnoseComplete, deliverComplete, deliverUnlocked }) {
  // Determine opacity for each segment based on completion
  const getSegmentStyle = (step, isComplete, isActive) => {
    const color = PROGRESS_COLORS[step]
    let opacity = 0.3 // Locked/future state
    
    if (isComplete) {
      opacity = 1
    } else if (isActive) {
      opacity = 0.6
    }
    
    return {
      backgroundColor: color,
      opacity,
    }
  }
  
  // Determine which step is active
  const describeActive = !describeComplete
  const detectActive = describeComplete && !detectComplete
  const diagnoseActive = detectComplete && !diagnoseComplete
  // Deliver is only active when unlocked (acknowledged or follow-up scheduled)
  const deliverActive = deliverUnlocked && !deliverComplete
  
  return (
    <div className="flex gap-1.5">
      <div 
        className="h-[4px] flex-1 rounded-full"
        style={{
          ...getSegmentStyle('describe', describeComplete, describeActive),
          boxShadow: '0 0 0 1px rgba(255,255,255,0.15)',
        }}
      />
      <div 
        className="h-[4px] flex-1 rounded-full"
        style={{
          ...getSegmentStyle('detect', detectComplete, detectActive),
          boxShadow: '0 0 0 1px rgba(255,255,255,0.15)',
        }}
      />
      <div 
        className="h-[4px] flex-1 rounded-full"
        style={{
          ...getSegmentStyle('diagnose', diagnoseComplete, diagnoseActive),
          boxShadow: '0 0 0 1px rgba(255,255,255,0.2)',
        }}
      />
      <div 
        className="h-[4px] flex-1 rounded-full"
        style={{
          ...getSegmentStyle('deliver', deliverComplete, deliverActive),
          boxShadow: '0 0 0 1px rgba(255,255,255,0.15)',
        }}
      />
    </div>
  )
}

// Modal component that renders to body
function Modal({ children, onClose }) {
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      {children}
    </div>,
    document.body
  )
}

function CaseHome() {
  const navigate = useNavigate()
  // v242.69.0 — `?spine=timeline` restores the pre-pager vertical stack byte for
  // byte. Read once at mount; never written, so it cannot drift mid-session.
  const spineLayout = useRef(
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('spine') === 'timeline'
      ? 'timeline' : 'pager'
  ).current
  const location = useLocation()
  const { currentCase, cases, clearCurrentCase, deleteCase, archiveCase, loadCases, updateCase, updateAndSaveCase } = useCaseStore()
  // PROD-1395: a pending "Allow access?" request (someone opened this case's share code) shows on the case itself.
  const openReq = useOpenRequest(currentCase?.id, isCodeSharedUnclaimed(currentCase))
  const { getSpecialty, getFocusArea } = useQuestionnaireStore()
  // ★ s117 (D529): a case the physician started with no area opens on the area picker (his areas + General
  //   complaint); the pick writes the area and Describe starts. utils/mdCaseArea owns the test.
  useEffect(() => {
    if (needsAreaPick(currentCase)) navigate('/specialties', { replace: true, state: { mdCaseId: currentCase.id } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCase?.id, currentCase?.specialtyName])
  const { profile } = useProfileStore()
  const { t, language, hasText } = useI18n()
  const appointmentErrorKey = (e) => appointmentErrorKeyFor(e, hasText) // PROD-1089: es withholds the physician_inactive sentence
  const { tfa } = useQuestionnaireTranslation()
  // MEET PA0: the booking-time recording question of a video visit (RecordingConsentSheet.jsx).
  const { askRecordingConsent, consentSheet } = useBookingRecordingConsent()
  const [showExitWarning, setShowExitWarning] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showRemindSheet, setShowRemindSheet] = useState(false)
  // s101 P1.2 (Dr. Rami 2026-10-12, Alex Build 65 x_20): the Case Report lives under Documents & records.
  const [reportViewerOpen, setReportViewerOpen] = useState(false)
  const [reportAgreementOpen, setReportAgreementOpen] = useState(false)
  // s99 D475 (Dr. Rami 2026-10-10): the office the Case Report was faxed to. Shown as "Sent to"
  //   in "Your visit" until the doctor ACCEPTS the case on the MD side, which is what sets
  //   "Your doctor" (claim -> the External->Orella conversion below). Read for a case with a code
  //   and no doctor yet; the server marks which send is current.
  // ★ PROD-1422 (R1422 M1): Case Home reads the sends exactly as the share hub does (casesAPI.faxSends +
  //   currentOfficeSend, for every case with a code, whatever doctorSource says: a faxed case can keep a local
  //   'orella'), and keeps "unknown" apart from "none". officeSend is undefined while loading, after a failed read
  //   or for another case; null only after a successful read that shows no live send; else the live send. The
  //   "Find a doctor" pill shows only on null, so a faxed case never offers it ahead of the hub's D476 ask.
  // ★ PROD-1422 (RR1422 M1): the read is skipped only when there is no code, or when the code is claimed AND a
  //   doctor is on the case. An unclaimed code is always read, also when the patient saved their own doctor's name
  //   (externalDoctor counts for hasAttendingDoctor but does not claim the code), so the pill matches the hub.
  //   "Sent to" stays hidden while a doctor is on the case (sentOffice below), exactly as before.
  const [officeRead, setOfficeRead] = useState({ id: null, send: undefined })
  const officeSend = officeRead.id != null && officeRead.id === currentCase?.id ? officeRead.send : undefined
  useEffect(() => {
    const c = currentCase
    const code = c && (c.shareCode || c.share_code)
    if (!c?.id || !code || (hasAttendingDoctor(c) && !isCodeSharedUnclaimed(c))) { setOfficeRead({ id: c?.id ?? null, send: undefined }); return }
    let alive = true
    import('../services/api').then(({ casesAPI }) => casesAPI.faxSends(c.id))
      .then((r) => { if (alive) setOfficeRead({ id: c.id, send: currentOfficeSend(r?.sends) }) })
      .catch(() => {}) // a failed read stays unknown (undefined): the pill stays hidden
    return () => { alive = false }
  }, [currentCase?.id, currentCase?.shareCode, currentCase?.doctorSource, currentCase?.status]) // eslint-disable-line react-hooks/exhaustive-deps
  const [showDocHandler, setShowDocHandler] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [archiveReason, setArchiveReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showCancelBridge, setShowCancelBridge] = useState(false)
  const [cancellingBridge, setCancellingBridge] = useState(false)

  // v138.3.0: Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  // ★★ v242.106.0 — CANCEL & WITHDRAW, reachable from the visit card.
  //   Dr. Rami, 2026-08-21: "add the cancel and withdraw link to the case home
  //   page in the appointment management area … reuse this link/function".
  //   It IS the same function — utils/withdrawCase.js — not a second one that
  //   nulls the same twenty fields and drifts from it later.
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const handleWithdrawCase = async () => {
    if (isWithdrawing) return
    setIsWithdrawing(true)
    // TEST 3.17 (patient push 3a): where to land is read from the case BEFORE it is cleared
    const caseBefore = currentCase
    try {
      await withdrawCaseFromDoctor(currentCase?.id, updateAndSaveCase, caseBefore)
      setShowWithdrawModal(false)
      setIsWithdrawing(false)
      navigate(withdrawDestination(caseBefore), { replace: true })
    } catch (err) {
      console.error('[CaseHome] Withdraw failed:', err)
      // ★ The modal STAYS OPEN on failure. Closing it would look like the
      //   withdrawal succeeded, and the case is still with the doctor.
      setIsWithdrawing(false)
    }
  }

  const [rescheduleType, setRescheduleType] = useState('initial') // 'initial' | 'follow_up'
  const [rescheduleSlots, setRescheduleSlots] = useState(null)
  // D7 (PD7-2): where each slot is (utils/bookableSlots.js slotPlacesOf); null for an API
  //   without `locations`, and then the reschedule sheet is what it was before D7.
  const [reschedulePlaces, setReschedulePlaces] = useState(null)
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState(null)
  const [rescheduleTime, setRescheduleTime] = useState(null)
  const [rescheduleVisitType, setRescheduleVisitType] = useState('in-person')
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false)
  const [rescheduleComplete, setRescheduleComplete] = useState(false)
  const [rescheduleError, setRescheduleError] = useState(null)
  // v242.87.0 (TF b34 #11): 'no-doctor' | 'scheduling-off' | 'none-open' | 'fetch-failed'
  const [rescheduleNoSlotsReason, setRescheduleNoSlotsReason] = useState(null)

  // Orella chat animation states
  const [chatShowLogo, setChatShowLogo] = useState(false)
  const [chatShowBubble, setChatShowBubble] = useState(false)
  const [chatShowDots, setChatShowDots] = useState(false)
  const [chatShowText, setChatShowText] = useState(false)
  const [chatShowContent, setChatShowContent] = useState(false)
  // v242.71.0 — the chat is no longer a thing that opens; it is on the page.
  // What remains is where to LOOK, so the deep-link scrolls instead of opening.
  const chatCardRef = useRef(null)
  const [agentInitialMessage, setAgentInitialMessage] = useState(null)

  // v220.2 — Auto-open Orella chat when arriving from a case_message notification
  // (?openChat=1). The notification deep-link lands the patient here, then the
  // effect fires the chat and cleans up the URL so refresh doesn't re-trigger.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('openChat') !== '1') return
    const timer = setTimeout(() => {
      chatCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const next = new URLSearchParams(searchParams)
      next.delete('openChat')
      setSearchParams(next, { replace: true })
    }, 400)
    return () => clearTimeout(timer)
  }, [searchParams, setSearchParams])

  // v226.9.2 (G2): Auto-advance to Diagnose when arriving from a finalized-care-plan
  // notification (?go=diagnose). Waits for the case to load, then routes onward and
  // cleans up the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('go') !== 'diagnose') return
    if (!currentCase?.id) return
    const next = new URLSearchParams(searchParams)
    next.delete('go')
    setSearchParams(next, { replace: true })
    navigate('/case/diagnose')
  }, [searchParams, setSearchParams, currentCase?.id, navigate])

  // s111: from the updated-summary notice (?go=conditions) -> the refreshed conditions list, once the
  //   case is loaded (the list read there marks the "Updated" pill seen). Same shape as ?go=diagnose.
  useEffect(() => {
    if (searchParams.get('go') !== 'conditions') return
    if (!currentCase?.id) return
    const next = new URLSearchParams(searchParams)
    next.delete('go')
    setSearchParams(next, { replace: true })
    navigate('/case/describe/conditions')
  }, [searchParams, setSearchParams, currentCase?.id, navigate])

  // v226.9.4 (G4): Auto-advance to the Deliver report flow when arriving from an
  // observation check-in notification (?openReport=1). Waits for the case to load,
  // forwards to Deliver (which auto-opens the check-in), and cleans up the URL.
  useEffect(() => {
    if (searchParams.get('openReport') !== '1') return
    if (!currentCase?.id) return
    const next = new URLSearchParams(searchParams)
    next.delete('openReport')
    setSearchParams(next, { replace: true })
    navigate('/case/deliver', { state: { openObsCheckIn: true } })
  }, [searchParams, setSearchParams, currentCase?.id, navigate])

  // Helper to open agent with optional pre-filled question
  // Sends straight into the thread that is already on screen. Nothing opens.
  const openAgentWith = (message) => {
    if (message) setAgentInitialMessage(message)
    chatCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  // Ref to always get latest case data in interval (avoids stale closure)
  const caseRef = useRef(currentCase)
  caseRef.current = currentCase

  // Auto-refresh case data on mount to get latest status/appointments
  useEffect(() => {
    const refreshCaseData = async () => {
      if (currentCase?.id && !currentCase.id.startsWith('local-')) {
        // v184.3: Skip immediate refresh if just checked in — local state is correct
        if (currentCase.status === 'checked_in') {
          console.log('[CaseHome] Skipping immediate refresh — just checked in')
          return
        }
        setIsRefreshing(true)
        try {
          console.log('[CaseHome] Refreshing case data, current status:', currentCase.status)
          console.log('[CaseHome] Current appointment request present:', !!currentCase.appointmentRequest) // MEET PA5: presence only
          await loadCases()
          console.log('[CaseHome] Case data refreshed, new appointment status:', caseRef.current?.appointmentRequest?.status)
        } catch (err) {
          console.error('Failed to refresh case:', err)
        }
        setIsRefreshing(false)
      }
    }
    refreshCaseData()
    
    // Set up periodic refresh for cases that might get updates from doctor
    const interval = setInterval(() => {
      const current = caseRef.current
      if (!current?.id) return
      
      const appointmentStatus = current.appointmentRequest?.status
      const caseStatus = current.status
      console.log('[CaseHome] Checking if refresh needed - case status:', caseStatus, 'appointment status:', appointmentStatus)
      
      // Only refresh if case is still awaiting doctor action or appointment confirmation
      const needsRefresh = 
        caseStatus === 'shared' || 
        (caseStatus === 'claimed' && appointmentStatus !== 'confirmed') ||
        (current.appointmentRequest && appointmentStatus !== 'confirmed' && caseStatus !== 'checked_in' && caseStatus !== 'scheduled')
      
      if (needsRefresh) {
        console.log('[CaseHome] Periodic refresh triggered - appointment status:', appointmentStatus)
        loadCases().then(() => {
          console.log('[CaseHome] Refresh completed')
        }).catch((e) => console.error('[CaseHome] Refresh failed:', e?.name))
      } else {
        console.log('[CaseHome] No refresh needed - appointment confirmed or no pending updates')
      }
    }, 15000) // Check every 15 seconds for faster updates
    
    return () => clearInterval(interval)
  }, [currentCase?.id])

  // Orella chat animation — replays when case phase changes
  // Compute phase inline to avoid dependency ordering issues
  const computePhase = () => {
    // hasConsult = true ONLY when MD has finalized (AVS or consult note exists)
    // diagnosis alone is NOT sufficient — it can come from AI pre-analysis during Describe
    const hasConsult = !!(currentCase?.avsContent || currentCase?.diagnose?.avsContent || currentCase?.afterVisitSummary || currentCase?.consultNote)
    const done = currentCase?.status === 'completed' || currentCase?.status === 'in_follow_up' || hasConsult
    if (done) return 6
    const st = currentCase?.status
    if (st === 'shared' || st === 'claimed' || st === 'in_progress') return 5
    const descDone = currentCase?.describeComplete || done
    const detDone = currentCase?.detectComplete || done
    if (descDone && detDone) return 4
    if (descDone && !detDone) return 3
    const started = !!((currentCase?.answers && Object.keys(currentCase.answers).length > 0) || currentCase?.voiceRecording || currentCase?.voiceUri || currentCase?.describeStarted)
    if (!descDone && started) return 2
    return 1
  }
  const chatPhase = computePhase()

  
  useEffect(() => {
    const key = `orella_case_chat_${currentCase?.id || 'none'}_phase${chatPhase}`
    const hasPlayed = sessionStorage.getItem(key)
    if (hasPlayed) {
      setChatShowLogo(true)
      setChatShowBubble(true)
      setChatShowText(true)
      setChatShowContent(true)
      return
    }
    // Reset animation states for new phase
    setChatShowLogo(false)
    setChatShowBubble(false)
    setChatShowDots(false)
    setChatShowText(false)
    setChatShowContent(false)
    const timers = []
    timers.push(setTimeout(() => setChatShowLogo(true), 300))
    timers.push(setTimeout(() => { setChatShowBubble(true); setChatShowDots(true) }, 500))
    timers.push(setTimeout(() => { setChatShowDots(false); setChatShowText(true) }, 1300))
    timers.push(setTimeout(() => { setChatShowContent(true); sessionStorage.setItem(key, 'true') }, 1600))
    return () => timers.forEach(clearTimeout)
  }, [currentCase?.id, chatPhase])

  // Check if this is an MD-initiated case that needs onboarding
  // MD-initiated cases need patient to complete: pharmacy selection + patient care agreement
  // ALSO handles external→orella conversion: when an Orella doctor claims an external share code
  useEffect(() => {
    if (!currentCase?.id) return
    
    // === EXTERNAL → ORELLA CONVERSION ===
    // If doctor_source is 'external' but an Orella doctor has claimed the case,
    // auto-convert to 'orella' path so the patient gets the full Orella experience
    const isExternalClaimed = (currentCase.doctorSource === 'external' || currentCase.doctor_source === 'external') &&
      (currentCase.status === 'claimed' || currentCase.status === 'scheduled' || currentCase.status === 'in_progress') &&
      !!(currentCase.doctor || currentCase.doctor_id || currentCase.sentToDoctor)
    
    // Guard: only run conversion once per case (prevents loop if API update is slow)
    const conversionKey = `orella_ext_converted_${currentCase.id}`
    const alreadyConverted = sessionStorage.getItem(conversionKey)
    
    if (isExternalClaimed && !alreadyConverted) {
      console.log('[CaseHome] 🔄 External→Orella conversion detected! Doctor claimed external share code.')
      console.log('[CaseHome] Converting doctor_source from external → orella')
      
      // Mark conversion as done BEFORE any async work
      sessionStorage.setItem(conversionKey, 'true')
      
      // Convert locally
      updateCase({ 
        doctorSource: 'orella', 
        doctor_source: 'orella',
      })
      
      // Sync conversion to API (fire-and-forget)
      import('../services/api').then(({ casesAPI }) => {
        casesAPI.update(currentCase.id, { doctor_source: 'orella' })
          .then(() => console.log('[CaseHome] ✅ doctor_source converted to orella on API'))
          .catch(err => console.warn('[CaseHome] API conversion sync failed:', err))
      })
      
      // Redirect to onboarding flow (appointment/pharmacy/agreement)
      console.log('[CaseHome] Redirecting to ShareScreen for onboarding after conversion')
      navigate('/case/share', { state: { caseId: currentCase.id, mdOnboard: true, convertedFromExternal: true } })
      return // Skip the normal onboarding check below
    }
    
    // === STANDARD MD-INITIATED ONBOARDING ===
    // Only for MD-INVITED cases (doctor added patient, not patient sharing with doctor).
    // Patient-initiated shares already complete onboarding during the share flow.
    const isPatientInitiatedShare = !!(currentCase.sentToDoctorAt || currentCase.shared_at) && 
      (currentCase.doctorSource === 'orella' || currentCase.doctor_source === 'orella' || currentCase.status === 'shared')
    
    const isMDInitiated = !isPatientInitiatedShare && (
      currentCase.doctor || currentCase.doctor_id || currentCase.sentToDoctor || 
      (currentCase.status === 'claimed' && !currentCase.describeComplete)
    )
    
    // Check if onboarding is complete (has agreement signed AND pharmacy selected)
    const hasCompletedOnboarding = currentCase.patientAgreement?.signed || 
      currentCase.onboardingComplete
    
    console.log('[CaseHome] MD Onboarding check:', {
      caseId: currentCase.id?.slice(-8),
      isMDInitiated,
      hasCompletedOnboarding,
      status: currentCase.status,
      hasDoctor: !!currentCase.doctor,
      hasAgreement: !!currentCase.patientAgreement?.signed
    })
    
    // If MD-initiated and hasn't completed onboarding, redirect to share screen in MD onboard mode
    if (isMDInitiated && !hasCompletedOnboarding) {
      console.log('[CaseHome] Redirecting to ShareScreen in MD Onboard mode')
      navigate('/case/share', { state: { caseId: currentCase.id, mdOnboard: true } })
    }
  }, [currentCase?.id, currentCase?.patientAgreement?.signed, currentCase?.onboardingComplete, currentCase?.doctor, currentCase?.doctorSource, currentCase?.status, navigate])

  // Check if there are urgent questions for this focus area
  const hasUrgentQuestions = useMemo(() => {
    if (!currentCase?.specialtyId || !currentCase?.focusAreaId) return false
    
    const focusArea = getFocusArea(currentCase.specialtyId, currentCase.focusAreaId)
    
    if (!focusArea?.questions) return false
    
    const categories = ['details', 'signs_symptoms', 'context', 'additional_factors']
    
    for (const category of categories) {
      const questions = focusArea.questions[category] || []
      for (const q of questions) {
        if (q.isUrgent === true || (q.text && q.text.toUpperCase().includes('URGENT'))) {
          return true
        }
      }
    }
    
    return false
  }, [currentCase?.specialtyId, currentCase?.focusAreaId, getFocusArea])

  // TEST 1.38 (P17 step 2) — with no current case, Case Home loads the list ONCE and only then
  //   decides. Before, every loadCases() here sat behind `currentCase?.id`, so a patient with
  //   cases who reached /case/home with none selected (seen after a PIN unlock) was told
  //   "No Cases Yet". The decision is caseHomeView's (utils/caseHomeState.js); it does not wait
  //   on the store's casesLoading, which rehydrate re-arms and only a load clears.
  const noCaseLoadStarted = useRef(false)
  const [noCaseLoadSettled, setNoCaseLoadSettled] = useState(false)
  useEffect(() => {
    if (currentCase || noCaseLoadStarted.current) return
    noCaseLoadStarted.current = true
    // No unmount guard on purpose: under StrictMode's mount-unmount-mount the ref keeps the
    //   second run from loading again, so a guard would leave the flag unset for ever.
    Promise.resolve()
      .then(() => loadCases())
      .catch((err) => console.error('[CaseHome] Loading cases with no current case failed:', err?.message || err))
      .finally(() => setNoCaseLoadSettled(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCase])

  // v143.5: Auto-open reschedule modal when navigated from My Visits
  // FHOOK: this hook sits ABOVE the `!currentCase` early return (rules of hooks: the hook count
  //   must not change when currentCase goes null -> set on the same instance). It does nothing
  //   while currentCase is null and runs when the case arrives, which is when it first ran before.
  useEffect(() => {
    if (!currentCase) return
    if (location.state?.openReschedule) {
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, '')
      // Small delay to let the page render first
      setTimeout(() => openRescheduleModal('initial'), 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, !!currentCase])

  if (!currentCase) {
    const noCaseView = caseHomeView({ currentCase, caseCount: Array.isArray(cases) ? cases.length : 0, loadSettled: noCaseLoadSettled })
    if (noCaseView === 'toList') return <Navigate to="/cases" replace />
    if (noCaseView === 'loading') {
      return (
        <div className="min-h-full bg-white flex flex-col">
          <OrellaLoader message={t('casesList.loadingCases')} />
        </div>
      )
    }
    return (
      <div className="min-h-full bg-white p-6 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{t('cases.noCases')}</p>
        <button 
          onClick={() => navigate('/home')}
          className="text-white px-6 py-3 rounded-full font-semibold"
          style={{ backgroundColor: ORELLA_BLUE }}
        >
          {t('home.startNewCase')}
        </button>
      </div>
    )
  }

  // v166.7.0: Check if MD has formally finalized (clicked finalize button).
  // The encounter's `finalized` flag is the source of truth — data presence alone
  // (AVS, consult note) does NOT count since those are generated during workflow
  // before the MD clicks finalize.
  // v242.170.0 — a fifth copy of the pointer-then-newest rule lived here; it is
  // the owner's `activeEncounter` exactly. See utils/caseEncounters.js.
  const currentEncounter = activeEncounter(currentCase)
  const encounterFinalized = currentEncounter?.finalized === true
  
  // hasConsultationData: true ONLY when encounter is formally finalized
  // OR when case DB status is in a finalized state (completed, in_follow_up, in_observation)
  const hasConsultationData = encounterFinalized || 
    ['completed', 'in_follow_up', 'in_observation'].includes(currentCase.status)

  // Case is "completed" if status is completed/in_follow_up OR encounter is finalized
  const isCompleted = currentCase.status === 'completed' || currentCase.status === 'in_follow_up' || hasConsultationData
  
  // describeComplete should be true if explicitly set OR if case is already completed/finalized
  const describeComplete = currentCase.describeComplete || isCompleted || false
  const detectComplete = currentCase.detectComplete || isCompleted || false
  
  // Status 'scheduled', 'follow_up', or 'in_follow_up' means follow-up was scheduled after consultation
  const hasScheduledFollowUp = currentCase.status === 'scheduled' || currentCase.status === 'follow_up' || currentCase.status === 'in_follow_up'
  
  // Check if patient has acknowledged the diagnosis
  const diagnoseAcknowledged = currentCase.diagnoseAcknowledged || false

  const isExternalPath = currentCase.doctorSource === 'external' || currentCase.doctor_source === 'external'

  // v229.4.0: Deliver (treatment & follow-up orchestration) requires a doctor who
  // is subscribed to Orella. The external/outside-MD path generates the Diagnose
  // section from the patient's uploaded note (source 'external_note_ai') but there
  // is no Orella MD — so Deliver stays LOCKED. If an Orella doctor later claims the
  // share code, the External→Orella conversion flips doctor_source to 'orella',
  // isExternalPath becomes false, and Deliver follows the normal flow (claiming = unlock).
  // v232.0.0: The AVS source is the durable signal that this case was finalized by the
  // outside-MD AI path (no Orella MD). It is written by BOTH the patient app and the API
  // when the uploaded note is analyzed, and it survives the diagnose_data smart-merge —
  // unlike doctorSource, which can be unset on some external cases (the v231 bug: Deliver
  // rendered active "In Progress" because isExternalPath was false despite the AI-generated
  // AVS). Gate on EITHER the external-path flag OR the AI source. The !hasOrellaDoctor clause
  // preserves the External→Orella conversion: once a real Orella doctor claims the share code,
  // doctorSource flips to 'orella' and Deliver unlocks into the normal managed flow.
  // v234.16.0: single source of truth for both gates — see utils/caseGating.js.
  // deliverNeedsOrellaMD keeps Deliver LOCKED for the whole outside-MD path (prevents the
  // v231 "In Progress" regression); deliverGatedSubscription controls the "remind your doctor"
  // surface, which only appears once the note is processed / the case is finalized.
  const deliverNeedsOrellaMD = deliverNeedsOrellaMDFn(currentCase)
  const deliverGatedSubscription = deliverGatedSubscriptionFn(currentCase, isCompleted)

  // Deliver section unlocks when: consultation complete AND (acknowledged OR follow-up scheduled)
  // …and an Orella-subscribed MD is on the case.
  const deliverUnlocked = isCompleted && (diagnoseAcknowledged || hasScheduledFollowUp) && !deliverNeedsOrellaMD

  const diagnoseAvailable = currentCase.status === 'shared' || currentCase.status === 'claimed' || currentCase.status === 'scheduled' || currentCase.status === 'checked_in' || currentCase.status === 'in_progress' || isCompleted || isExternalPath
  const canShare = describeComplete
  
  // ============ PHASE 1: UPDATED STATUS LOGIC ============
  // Determine status for the new badge system
  // v153.0: Use canonical DB statuses. Backward-compat shims for ready_to_share and scheduled
  // until Phase 2 API promotes these statuses server-side.
  const getStatus = () => {
    // v124.8.0: Observation = active monitoring period
    if (currentCase.status === 'in_observation') return 'in_observation'
    // In Follow-Up = consultation complete, awaiting follow-up
    if (currentCase.status === 'in_follow_up') return 'in_follow_up'
    // Finalized = consultation complete (fully closed)
    if (hasConsultationData || currentCase.status === 'completed') return 'finalized'
    if (hasScheduledFollowUp) return 'finalized'
    // Scheduled = appointment confirmed (DB status or backward-compat shim)
    if (currentCase.status === 'checked_in') return 'checked_in'
    // ★ PROD-983: the decline of a held reschedule of the confirmed initial visit leaves the case
    //   column 'scheduled'; with the request declined nothing is scheduled. 'claimed' opens the same
    //   stages (see PROD-921 below), so only the wording changes.
    // ★ PROD-1137: the same for a visit the physician cancelled (the API moves the case to 'claimed';
    //   this covers a case whose status write did not land).
    if (currentCase.status === 'scheduled') return requestDeclined(currentCase.appointmentRequest) || requestCancelledByPhysician(currentCase.appointmentRequest) ? 'claimed' : 'scheduled'
    // ★ PROD-921: not while a row waits in pending_reschedule (same guard as the caseBadges shim).
    //   'claimed' and 'scheduled' open the same stages and check-in (CHECK_IN_STATUSES), so only
    //   the wording that reads this status changes.
    if (currentCase.status === 'claimed' && (currentCase.appointmentStatus === 'confirmed' || currentCase.appointmentRequest?.status === 'confirmed' || currentCase.appointment_status === 'confirmed')
      && !hasPendingReschedule(currentCase)) return 'scheduled'
    // In Progress = doctor has started working on case
    if (currentCase.status === 'in_progress') return 'in_progress'
    // Claimed = doctor has claimed
    if (currentCase.status === 'claimed') return 'claimed'
    // Shared = shared but not claimed
    if (currentCase.status === 'shared') return 'shared'
    // Submitted = external doctor path (Visit Prep generated)
    if (currentCase.status === 'submitted') return 'submitted'
    // Ready to Share = describe complete but not shared (DB status or backward-compat shim)
    if (currentCase.status === 'ready_to_share') return 'ready_to_share'
    if (describeComplete) return 'ready_to_share'
    // Draft = still working on it
    return 'draft'
  }
  
  // ★★ v242.98.0 (TF b37 #2) — getStatus() reads currentCase.status, which does
  //   NOT move when a case is shared by code. One correction, shared with
  //   utils/caseBadges so the header pill and the Cases-list pill cannot drift.
  const status = resolveShareStatus(getStatus(), currentCase)

  // v242.69.0 — MOVED here, below `const status = getStatus()`, and re-pointed at the
  // DERIVED status. It used to sit above getStatus() and read currentCase.status raw,
  // which is a different value: a finalized consult whose DB row still says 'claimed'
  // derives to 'finalized', so the chat correctly refused check-in while THIS memo told
  // the Ask Orella agent check-in was open. Same window, two answers.
  // v235.16.0 — temporal signals for the Ask Orella chat opener. Mirrors the hero
  // card's check-in awareness so the agent greets for where the case is IN TIME
  // (appointment imminent / confirmed) rather than only its 4D phase. Component
  // scope so it's available where caseContext is assembled.
  const agentTemporal = (() => {
    const st = status
    const apptReq = currentCase?.appointmentRequest
    const apptDate = apptReq?.confirmedDate || apptReq?.requestedDate || ''
    const apptTime = apptReq?.confirmedTime || apptReq?.requestedTime || ''
    // v242.69.0 — one predicate, shared with the chat block and the action row.
    const checkInEligible = isCheckInEligible(st, apptReq, new Date(), currentCase) // PROD-1007: a held reschedule's new time opens no check-in
    let appointmentLabel = ''
    // PROD-983: a declined request's time is no visit; the group header names no time for it.
    // PROD-1137: nor does the time of a visit the physician cancelled.
    if (apptDate && !requestDeclined(apptReq) && !requestCancelledByPhysician(apptReq)) {
      try {
        appointmentLabel = formatDate(apptDate, { weekday: 'short', month: 'short', day: 'numeric' })
        if (apptTime) {
          const [h, m] = apptTime.split(':').map(Number)
          appointmentLabel += ` at ${h > 12 ? h - 12 : (h || 12)}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`
        }
      } catch { /* no-op */ }
    }
    return { checkInEligible, appointmentLabel }
  })()

  // Check if this is a brand new case (nothing done yet)
  const isNewCase = !describeComplete && !detectComplete
  const isShared = ['shared', 'claimed', 'in_progress', 'scheduled', 'checked_in'].includes(status)
  // v224.7.2: A case shared by code keeps server status 'ready_to_share' (the share
  // endpoint never bumps status); the presence of a share_code means it IS shared and
  // awaiting a doctor. Without this it falls through to phase 3 ('share next') and looks
  // unshared on reload. A genuinely withdrawn case has its share_code cleared server-side.
  const hasShareCode = !!(currentCase.shareCode || currentCase.share_code)
  const isCodeShared = hasShareCode && !(currentCase.doctor || currentCase.doctor_id)
  
  // Determine if Describe has been started (any answers saved, voice recorded, etc.)
  const hasStartedDescribe = !!(
    (currentCase.answers && Object.keys(currentCase.answers).length > 0) ||
    currentCase.voiceRecording ||
    currentCase.voiceUri ||
    currentCase.describeStarted
  )
  
  // ============ 6 CASE PHASES ============
  // Phase 1: Getting Started — brand new, Describe not yet started
  // Phase 2: Complete Your Case — Describe started but NOT submitted
  // Phase 3: Share Case — Describe submitted, not yet shared (v142.7: moved before Detect)
  // Phase 4: Submit Detect — Shared, Detect not yet submitted
  // Phase 5: Being Reviewed — Shared + Detect done, waiting for MD to finalize
  // Phase 6: Consult Completed — MD finalized, results available
  const getCasePhase = () => {
    // v138.0.0: STATUS-BASED FLOOR GATE
    const statusFloor = (() => {
      const s = currentCase.status
      if (['completed', 'in_follow_up', 'in_observation'].includes(s)) return 6
      // v224.7.2: code-shared (ready_to_share + share_code, no doctor) is a shared state.
      // ★★★ v242.98.0 — THIS WAS THE THIRD COPY OF THE RULE, and it is the one that
      //   was RIGHT. The phase machine has known since v224.7.2 that a code-shared
      //   case is shared; getStatus() and caseBadges never did. That is exactly why
      //   the screen in TF b37 #2 showed Detect in progress (phase 4, correct) under
      //   a pill reading "Ready to Share" (wrong) — two machines, one case, two
      //   answers. Folded onto the shared predicate so the next edit moves all three.
      //   ★ isCodeSharedUnclaimed also treats sentToDoctorName as claimed, which the
      //     hand-rolled test did not. Not a widening in practice: the store derives
      //     that name FROM c.doctor (stores/index.js:1210), so it cannot be set
      //     without the doctor object this test already checked — only a stale local
      //     cache can carry it alone, and there a doctor genuinely was on the case.
      if (s === 'ready_to_share' && isCodeSharedUnclaimed(currentCase)) {
        if (!describeComplete) return 0
        if (detectComplete) return 5
        return 4
      }
      if (['shared', 'submitted', 'claimed', 'in_progress', 'scheduled', 'checked_in'].includes(s)) {
        // v192.3: MD-created cases have in_progress status but patient hasn't done clinical work.
        // Don't force phase 4 if Describe isn't even done — patient is at phase 1.
        if (!describeComplete) return 0  // Let derivedPhase determine the actual phase
        if (detectComplete) return 5
        return 4 // Shared but Detect pending
      }
      return 0
    })()

    const derivedPhase = (() => {
      if (isCompleted) return 6                                                         // MD finalized
      if ((status === 'shared' || status === 'claimed' || status === 'in_progress' || status === 'scheduled' || status === 'checked_in') && detectComplete) return 5 // Shared + Detect done
      if (isExternalPath && describeComplete && detectComplete) return 5                // External path: ready for diagnose upload
      // v192.4: Only jump to phase 4 if patient has actually completed Describe.
      // MD-created cases have in_progress status but patient hasn't done clinical work yet.
      if ((status === 'shared' || status === 'claimed' || status === 'in_progress' || status === 'scheduled' || status === 'checked_in') && describeComplete) return 4 // Shared + Describe done, Detect pending
      // v192.6: MD-created cases — doctor already assigned, onboarding already done. Skip share phase.
      if (describeComplete && !detectComplete && (currentCase.onboardingComplete || currentCase.patientAgreement?.signed) && (currentCase.doctor || currentCase.doctor_id)) return 4
      // v224.7.2: a code-shared case is awaiting a doctor, not "share next"
      if (isCodeShared && describeComplete && detectComplete) return 5
      if (isCodeShared && describeComplete) return 4
      if (describeComplete && !isShared && !detectComplete) return 3                    // Describe done, share next
      if (describeComplete && detectComplete && !isShared) return 3                     // Both done but not shared — unlikely but safe
      if (!describeComplete && hasStartedDescribe) return 2                             // Describe started, not finished
      return 1                                                                          // Brand new
    })()

    // Never regress below what status guarantees
    const phase = Math.max(statusFloor, derivedPhase)
    if (statusFloor > derivedPhase) {
      console.log(`[CaseHome] Status floor gate: status='${currentCase.status}' forced phase ${statusFloor} (derived was ${derivedPhase})`)
    }
    return phase
  }
  const casePhase = getCasePhase()

  // v229.3.0: External (outside-MD) path accepts the post-visit note from the
  // moment the case is code-shared — Detect is optional, so don't gate on it.
  // Open at shared (phase 4) and shared+Detect-done (phase 5); not after the
  // case is finalized (phase 6). This is what keeps the Diagnose card unlocked
  // and showing "Upload Doctor's Notes" on reload instead of persisting Locked.
  const externalUploadOpen = isExternalPath && (casePhase === 4 || casePhase === 5)

  // ============ ENCOUNTER LOOP (v120) ============
  // When currentEncounter >= 2, the 4D grid adapts based on encounterPhase
  const encounterNum = currentEncounterNumber(currentCase)
  const encounterPhase = currentCase.encounterPhase || ''
  const isFollowUpEncounter = encounterNum >= 2
  
  // v124.3.0: Detect observation encounter category for section rendering
  // v135.1.0: Use shared resolveActiveEncounter helper for consistency
  // ★★★ v242.188.0 — THIS LOCAL WAS NAMED `activeEncounter` AND IT SHADOWED THE IMPORT ON
  //   LINE 2, WHICH LINE 529 CALLS. A `const` hoists to the top of its function scope in the
  //   temporal dead zone, so the call 215 lines ABOVE this declaration resolved to THIS
  //   binding — not the import — and threw `Cannot access 'activeEncounter' before
  //   initialization` on every render of every case.
  //   ★★ NEITHER HALF WAS WRONG WHEN IT WAS WRITTEN. This local is from 2026-06-27; the
  //   import arrived on 2026-09-07 with D414, which made `activeEncounter` the OWNER of
  //   "which encounter is the patient standing in" and pulled it into the files that needed
  //   it. THE COLLISION IS WHAT AN OWNER COSTS: giving a shared idea its true name means the
  //   name is now taken, and the file that already used it locally does not complain — it
  //   silently wins.
  //   ★ The local keeps a name that says what it is: the encounter ROW resolved for
  //   category checks, not the owner's answer.
  const activeEncRow = resolveActiveEncounter(currentCase) || {}
  const activeEncCategory = activeEncRow.category || activeEncRow.appointment_category || ''
  // v125.2.0: After observation→follow_up bridge, encounters array may be stale. Don't treat as observation if case is already in_follow_up with observationFinding.
  const isObservationBridged = currentCase.status === 'in_follow_up' && currentCase.appointmentRequest?.observationFinding === true
  const isObservationEncounter = !isObservationBridged && (activeEncCategory === 'observation' || currentCase.status === 'in_observation')

  // v121.8.0: Pending appointment detection — look at the current encounter's appointment
  // The appointments array contains all appointments; find the one for the current encounter.
  const allAppointments = currentCase.appointments || []
  const currentEncounterAppointment = allAppointments.find(a => 
    (a.encounter_number || a.encounterNumber) === encounterNum && 
    a.appointment_category !== 'initial' &&
    !isOutsideAppointment(a)
  )
  // Pending reopen = current encounter has a pending appointment (patient waiting for MD confirmation)
  // ★ PROD-921 (review r_921 S1-1): this flag GATES the stages (getStepStatus, the Diagnose and
  //   Deliver tiles), so it stays on the raw 'pending' status. A pending_reschedule row is NOT
  //   folded in here: nothing moves such a row on (no md-panel approve path, API finalize leaves
  //   it), so a held, finalized follow-up would lock Deliver for good. The display-only flag
  //   followUpAwaitsReschedule below changes the follow-up line's tag and icon, nothing else.
  const hasPendingReopen = (currentEncounterAppointment?.status === 'pending' && encounterNum >= 2)
    || (currentCase.appointmentRequest?.status === 'pending' && currentCase.appointmentRequest?.reopened)  // legacy fallback
    || (currentCase.appointmentRequest?.status === 'pending' && currentCase.appointmentRequest?.observationFinding)  // v124.0.0: observation finding

  // ★ PROD-921: DISPLAY ONLY — never read by a stage, step or lock. The current follow-up row waits
  //   on the physician for a reschedule (pending_reschedule, manual_review) and its encounter is not
  //   finalized yet: the follow-up line shows the amber Pending tag and clock instead of the green
  //   Confirmed ones. Once the encounter is finalized the line reads as before (the visit is held).
  const followUpEncounterRow = (currentCase.encounters || []).find(e => (e.encounter_number || e.encounterNumber) === encounterNum)
  const followUpAwaitsReschedule = encounterNum >= 2
    && currentEncounterAppointment?.status === PENDING_RESCHEDULE
    && followUpEncounterRow?.finalized !== true

  // v125.2.0: Debug logging for bridge date tracking
  if (hasPendingReopen) {
    console.log('[CaseHome] PENDING REOPEN date debug:', {
      hasPendingReopen,
      hasCurrentEncounterAppointment: !!currentEncounterAppointment,
      currentEncounterAppointmentStatus: currentEncounterAppointment?.status ?? null,
      hasAppointmentRequest: !!currentCase.appointmentRequest,
      appointmentRequestStatus: currentCase.appointmentRequest?.status ?? null,
      encounterNum,
      allAppointmentsCount: allAppointments.length,
      allAppointmentsSummary: allAppointments.map(a => ({ id: a.id?.slice(-8), enc: a.encounter_number, hasCat: !!a.appointment_category, status: a.status })),
    })
  }

  // v121.8.7: Confirmed reopen = MD confirmed appointment, now in standard follow-up pre-visit flow.
  // Diagnose should show as viewable/pending (prior AVS available) but NOT complete/acknowledged.
  const isConfirmedReopen = !hasPendingReopen && isFollowUpEncounter
    && (currentCase.appointmentRequest?.reopened === true || currentCase.appointmentRequest?.observationFinding === true)  // v124.0.0: observation finding
    && currentCase.appointmentRequest?.status === 'confirmed'
    && encounterPhase === 'deliver_active'

  // Get step status for progress bar
  const getStepStatus = (step) => {
    // Encounter 2+ override
    if (isFollowUpEncounter) {
      switch(step) {
        case 'describe': return 'complete'
        case 'detect': return 'complete'
        case 'diagnose':
          // v121.6.3: During pending reopen, Diagnose shows as active/pending
          if (hasPendingReopen) return 'active'
          // v126.7.0: Confirmed follow-up (observation bridge or reopen) — Diagnose is active (viewable, prior AVS)
          if (isConfirmedReopen) return 'active'
          if (encounterPhase === 'diagnose_pending') return 'active'
          if (encounterPhase === 'diagnose_active') return 'active'
          if (encounterPhase === 'diagnose_acknowledged' || encounterPhase === 'deliver_active') return 'complete'
          if (encounterPhase === 'deliver_submitted') return 'complete'
          return 'locked'
        case 'deliver':
          // v234.15.1: MD-agnostic case (no Orella MD) — Deliver stays locked even in a
          // finalized follow-up encounter; treatment/follow-up needs a subscribed doctor.
          if (deliverNeedsOrellaMD) return 'locked'
          // v121.6.3: Lock when pending reopen
          if (hasPendingReopen) return 'locked'
          // v126.7.0: Confirmed follow-up — Deliver LOCKED until MD finalizes this encounter
          if (isConfirmedReopen) return 'locked'
          if (encounterPhase === 'deliver_active') return 'active'
          if (encounterPhase === 'deliver_submitted') return 'complete'
          if (encounterPhase === 'diagnose_active' || encounterPhase === 'diagnose_acknowledged') return 'locked'
          if (encounterPhase === 'diagnose_pending') return 'locked'
          if (encounterPhase === 'complete' && currentCase.status !== 'in_follow_up') return 'complete' // v121.4.0: truly closed
          if (encounterPhase === 'complete' && currentCase.status === 'in_follow_up') return 'locked' // reopened but not yet confirmed
          if (currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete') return 'complete'
          return 'locked'
        default: return 'locked'
      }
    }

    // Standard encounter 1 logic
    switch(step) {
      case 'describe':
        return describeComplete ? 'complete' : 'active'
      case 'detect':
        if (casePhase <= 2) return 'locked'
        return (detectComplete && casePhase === 6) ? 'complete' : 'active'
      case 'diagnose':
        if (casePhase < 4 && !isExternalPath) return 'locked'
        if (isExternalPath && casePhase < 4) return 'locked'
        if (casePhase === 6 && diagnoseAcknowledged) return 'complete'
        return 'active'
      case 'deliver':
        if (casePhase < 6 || !diagnoseAcknowledged || deliverNeedsOrellaMD) return 'locked'
        if (currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete') return 'complete'
        return 'active'
      default:
        return 'locked'
    }
  }

  // v242.92.0 (TF b37 #10 #11 #12 #13) — the stage card's body line.
  //   StagePager.jsx read `section.blurb` and NOTHING in the repo ever wrote it,
  //   so the `|| section.subtitle` fallback fired on every card and each one
  //   printed its own subtitle twice: "Detect / Examination / Examination".
  //   Four reports, one dead `||`. The resolver lives in components/casehome/
  //   stageModel.js as plain JS so `scripts/check-stage-model.mjs` can import it
  //   and call it — an arm on source text would only have been a guess.
  const stageBlurbs = resolveStageBlurbs(currentCase, {
    isCompleted,
    t,
    carePlan: resolveCarePlan(currentCase),
  })

  const sections = isFollowUpEncounter ? [
    // ============ ENCOUNTER 2+ SECTIONS ============
    {
      id: 'describe',
      title: t('describe.title'),
      subtitle: t('caseHome.initialIntake'),
      blurb: stageBlurbs.describe,
      icon: FileText,
      complete: true,
      available: false,
      locked: true,
      viewable: true,
      trailLabel: t('caseHome.trailViewSummary', 'View summary'), // s99 D477
      summaryUpdated: hasUnseenSummaryUpdate(currentCase), // s111 (TF b78)
      path: '/case/describe/summary',
    },
    {
      id: 'detect',
      title: t('detect.title'),
      subtitle: t('caseHome.initialPhotosDocs'),
      blurb: stageBlurbs.detect,
      icon: Camera,
      complete: true,
      available: false,
      locked: true,
      viewable: true,
      hasContent: true,
      path: '/case/detect',
    },
    {
      id: 'diagnose',
      title: t('diagnose.title'),
      subtitle: hasPendingReopen ? t('caseHome.subtitlePendingFollowUp')
        : isConfirmedReopen ? t('caseHome.subtitleConfirmedAwaitingResults')
        : encounterPhase === 'diagnose_pending' ? t('caseHome.subtitleConfirmedAwaitingResults')
        : encounterPhase === 'diagnose_active' ? t('caseHome.subtitleReviewFollowUp')
        : encounterPhase === 'deliver_active' ? t('caseHome.subtitlePreviousVisitSummary')
        : t('caseHome.subtitleFollowUpDiagnosis'),
      blurb: stageBlurbs.diagnose,
      icon: Stethoscope,
      // v121.8.7: During confirmed reopen, Diagnose is viewable but NOT complete — prior AVS available, no new diagnosis yet
      complete: !hasPendingReopen && !isConfirmedReopen && ['diagnose_acknowledged', 'deliver_active', 'deliver_submitted', 'complete'].includes(encounterPhase),
      available: encounterPhase === 'diagnose_active' && !hasPendingReopen,
      locked: false,
      viewable: true, // Always viewable in encounter 2+ (prior AVS)
      pending: hasPendingReopen || isConfirmedReopen || (encounterPhase === 'diagnose_pending' && !hasPendingReopen),
      needsReview: encounterPhase === 'diagnose_active' && !hasPendingReopen,
      path: '/case/diagnose',
    },
    {
      id: 'deliver',
      title: t('deliver.title'),
      subtitle: hasPendingReopen ? t('caseHome.subtitleLockedAwaitingConfirmation')
        : isObservationEncounter ? t('caseHome.subtitleMonitoring')
        : isConfirmedReopen ? t('caseHome.subtitleLockedAwaitingFollowUp')
        : encounterPhase === 'diagnose_pending' ? t('caseHome.subtitleLockedAwaitingResults')
        : encounterPhase === 'diagnose_active' ? t('caseHome.subtitleLockedReviewDiagnose')
        : encounterPhase === 'deliver_submitted' ? t('caseHome.subtitleSubmitted')
        : encounterPhase === 'deliver_active' ? t('caseHome.subtitleTreatmentFollowUp')
        : encounterPhase === 'complete' ? t('caseHome.subtitleCareComplete')
        : t('caseHome.subtitleNextSteps'),
      blurb: stageBlurbs.deliver,
      icon: Send,
      // v234.15.1: MD-agnostic case (no Orella MD) — Deliver is never complete/active; it
      // stays locked (canonical pastel-green) with the subscription disclaimer block beneath.
      complete: !deliverNeedsOrellaMD && (!isConfirmedReopen && (encounterPhase === 'deliver_submitted' || (encounterPhase === 'complete' && currentCase.status !== 'in_follow_up' && !hasPendingReopen) || currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete')),
      available: !deliverNeedsOrellaMD && (!isConfirmedReopen && !hasPendingReopen && ((encounterPhase === 'deliver_active') || (encounterPhase === 'complete' && currentCase.status !== 'in_follow_up'))),
      // v126.7.0: Lock for confirmed reopen AND pending reopen
      locked: deliverNeedsOrellaMD || hasPendingReopen || isConfirmedReopen || !['deliver_active', 'deliver_submitted', 'complete'].includes(encounterPhase),
      viewable: false,
      pending: false, // NOT amber — green locked state
      // v124.3.0: Observation encounters use amber styling for active Deliver
      observation: isObservationEncounter,
      deliverState: currentCase.deliverState || (encounterPhase === 'deliver_submitted' ? 'submitted' : encounterPhase === 'complete' ? 'complete' : null),
      path: '/case/deliver',
    },
  ] : [
    {
      id: 'describe',
      title: t('describe.title'),
      subtitle: t('describe.subtitle'),
      blurb: stageBlurbs.describe,
      icon: FileText,
      complete: describeComplete,
      // Phase 1-2: active (editable). Phase 3+: viewable review only.
      available: casePhase <= 2,
      locked: casePhase >= 3 && casePhase < 6 ? false : casePhase === 6, // viewable in 3-5, locked in 6
      viewable: describeComplete, // Can view review screen after describe is done
      // s99 D477 (Build 61 TestFlight): a finished Describe says "View summary" and opens it.
      trailLabel: describeComplete ? t('caseHome.trailViewSummary', 'View summary') : undefined,
      summaryUpdated: describeComplete && hasUnseenSummaryUpdate(currentCase), // s111 (TF b78)
      path: describeComplete ? '/case/describe/summary' : '/case/describe/voice',
    },
    {
      id: 'detect',
      title: t('detect.title'),
      subtitle: t('detect.subtitle'),
      blurb: stageBlurbs.detect,
      icon: Camera,
      // Only mark as "complete" in phase 6 (finalized). In phases 4-5, detect remains active
      complete: detectComplete && casePhase === 6,
      // v142.7.0: Phases 1-3: locked (must share first). Phases 4-5: active. Phase 6: locked.
      available: casePhase >= 4 && casePhase <= 5,
      locked: casePhase <= 3 || casePhase === 6,
      viewable: detectComplete && casePhase === 6,
      hasContent: detectComplete,
      // s99 D477: Detect done but the doctor has not finished the case: still open for more.
      trailLabel: detectComplete && casePhase >= 4 && casePhase <= 5 ? t('caseHome.trailViewAdd', 'View / Add') : undefined,
      // s101: submitted — still tappable (View / Add), never where Case Home lands (stageModel).
      submitted: detectComplete && casePhase >= 4 && casePhase <= 5,
      path: '/case/detect',
    },
    {
      id: 'diagnose',
      title: t('diagnose.title'),
      subtitle: externalUploadOpen ? 'Upload Doctor\'s Notes' : t('diagnose.subtitle'),
      blurb: stageBlurbs.diagnose,
      icon: Stethoscope,
      complete: isCompleted && diagnoseAcknowledged,
      // Phase 4: viewable pending (scheduled, can check in). Phase 5: pending review.
      // Phase 6 pre-acknowledge: needs review. Phase 6 post-acknowledge: complete.
      available: (casePhase === 6 && diagnoseAcknowledged) || externalUploadOpen,
      locked: casePhase < 4,
      viewable: casePhase >= 4,
      pending: (casePhase === 4 || casePhase === 5) && !isExternalPath,
      // s99 D475: a doctor has ACCEPTED the case and the consult is still ahead -> "Pending Consult".
      consultPending: (casePhase === 4 || casePhase === 5) && !isExternalPath && hasAttendingDoctor(currentCase),
      externalUpload: externalUploadOpen,
      needsReview: casePhase === 6 && !diagnoseAcknowledged,
      path: '/case/diagnose',
    },
    {
      id: 'deliver',
      title: t('deliver.title'),
      // v234.15.0: gated card keeps the SHORT canonical subtitle; the long subscription
      // disclaimer now lives in the grouped block beneath the Deliver box, not in-card.
      subtitle: deliverGatedSubscription ? t('deliver.subtitle')
        : casePhase === 6 && !diagnoseAcknowledged ? t('caseHome.subtitleLockedReviewDiagnose')
        : currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete' ? t('caseHome.subtitleCareComplete')
        : t('deliver.subtitle'),
      blurb: stageBlurbs.deliver,
      icon: Send,
      // v242.92.0 (TF b37 #10) — was `currentCase.deliverState === 'all-done' ||
      //   ... === 'complete'`. deliverState is written ONLY by DeliverScreen.jsx:1364
      //   and is undefined until the patient opens Deliver, so this evaluated false
      //   on every cold launch and the card reverted to Locked even for a resolved
      //   case — which is what his screenshot shows. stageBlurbs.resolved ORs in the
      //   care-plan disposition, which comes back from the API.
      complete: stageBlurbs.resolved,
      // Only available after consultation completed (phase 6) AND acknowledged AND an Orella MD is on the case
      available: casePhase === 6 && diagnoseAcknowledged && !deliverNeedsOrellaMD,
      locked: casePhase < 6 || !diagnoseAcknowledged || deliverNeedsOrellaMD,
      viewable: false,
      // Granular deliver status for pill display
      deliverState: currentCase.deliverState || null,
      path: '/case/deliver',
    },
  ]
  
  // Get appointment info from case
  // ★ PROD-1160 (review M1): the patient's outside visit (source 'manual_external') is never "the" visit;
  //   live API 369 serves it as case.appointment when it is the case's only row.
  const appointment = [currentCase.scheduledAppointment, currentCase.appointment].find(a => a && !isOutsideAppointment(a))
  // allAppointments already declared above (line 508)
  const completedAppointments = allAppointments.filter(a => a.status === 'completed')
  const upcomingAppointment = appointment || allAppointments.find(a => a.status === 'scheduled' && !isOutsideAppointment(a))
  
  // Get original appointment request (initial consultation appointment)
  // Check multiple sources: appointmentRequest, appointment table record, or appointments array
  const originalAppointment = (() => {
    // Helper to check if appointment is a follow-up or reopened
    // ★ PROD-921 (review r_921 N1): hoisted above step 1 so the request path's row lookup skips an
    //   uncategorised legacy follow-up row too (that row now decides the line's status).
    const isFollowUpOrReopened = (apt) => {
      const type = (apt.type || apt.appointment_type || '').toLowerCase()
      const category = (apt.appointment_category || '').toLowerCase()
      return type.includes('follow') || apt.is_followup === true || apt.id?.startsWith('followup-')
        || category === 'reopened' || category === 'follow_up'
    }

    const initialPool = rowsOfCurrentPhysician(currentCase, allAppointments) // PROD-1020

    // 1. First check appointmentRequest (patient-initiated flow)
    // v121.6.4: Skip if this is a reopened follow-up request — that's NOT the initial appointment
    // v125.0.3: Also skip if this is an observation finding bridge request
    if (currentCase.appointmentRequest && !currentCase.appointmentRequest.reopened && !currentCase.appointmentRequest.observationFinding) {
      // v154.4.2: Look up the DB appointment id from allAppointments for deep linking
      // ★ PROD-1020: only the current physician's rows (a reactivated case keeps the old physician's
      //   history in the list); a pending_reschedule row wins over an earlier confirmed one.
      const matchingApt = initialRowFor(initialPool.filter(a =>
        (a.appointment_category === 'initial' || !a.appointment_category) &&
        !isFollowUpOrReopened(a) &&
        a.source !== 'manual_external' && // PROD-1160: the patient's outside visit is no Orella visit
        a.status !== 'cancelled'
      ))
      // ★ PROD-921: in manual_review the API syncs the request as 'modified' (new confirmedDate)
      //   while the ROW says 'pending_reschedule'. The row wins: the new time is a pending request.
      // ★ PROD-983: a declined request stays declined — a stale local pending_reschedule row (the
      //   server drops the rejected row) must not turn it back into a pending request.
      const rowPending = !requestDeclined(currentCase.appointmentRequest) && matchingApt?.status === PENDING_RESCHEDULE
      return { ...currentCase.appointmentRequest, id: matchingApt?.id || currentCase.appointmentRequest.id, ...(rowPending ? { status: 'pending' } : {}) }
    }

    // 2. Check for initial appointment in appointments array (MD-initiated flow)
    const initialAppointments = initialPool.filter(a => !isFollowUpOrReopened(a) && a.source !== 'manual_external') // PROD-1160 (review M1)
    const sortedByDate = initialAppointments.sort((a, b) => {
      const dateA = new Date(a.date || a.created_at)
      const dateB = new Date(b.date || b.created_at)
      return dateA - dateB
    })
    // ★ PROD-1020 (review N2): same pending_reschedule preference as path 1 (PROD-1008 shape, no request).
    const initialFromArray = initialRowFor(sortedByDate)
    
    if (initialFromArray) {
      // Transform to appointmentRequest format for consistent rendering
      return {
        id: initialFromArray.id,
        status: initialFromArray.status === 'scheduled' ? 'confirmed' : asPendingRequest(initialFromArray.status),
        confirmedDate: initialFromArray.date,
        confirmedTime: initialFromArray.time,
        confirmedType: initialFromArray.type || initialFromArray.visitType,
        appointmentType: initialFromArray.type || initialFromArray.visitType,
        zoomLink: initialFromArray.zoom_link || initialFromArray.zoomLink,
        meetingLink: initialFromArray.zoom_link || initialFromArray.zoomLink,
      }
    }
    
    // 3. Check scheduledAppointment or appointment (fallback for MD-scheduled)
    // ★ PROD-1160 (review M1): an outside row served here (live API 369, only row) is no Orella visit.
    const directAppointment = [currentCase.scheduledAppointment, currentCase.appointment].find(a => a && a.source !== 'manual_external')
    if (directAppointment && !isFollowUpOrReopened(directAppointment)) {
      return {
        id: directAppointment.id,
        status: directAppointment.status === 'scheduled' ? 'confirmed' : asPendingRequest(directAppointment.status),
        confirmedDate: directAppointment.date,
        confirmedTime: directAppointment.time,
        confirmedType: directAppointment.type || directAppointment.visitType,
        appointmentType: directAppointment.type || directAppointment.visitType,
        zoomLink: directAppointment.zoom_link || directAppointment.zoomLink,
        meetingLink: directAppointment.zoom_link || directAppointment.zoomLink,
      }
    }
    
    return null
  })()
  
  // Get follow-up appointment data
  const followUpData = (() => {
    // PROD-903: presence only — the follow_up JSONB holds the clinical plan and can hold a visit link.
    console.log('[CaseHome] Follow-up data sources:', {
      followUp: !!currentCase.followUp,
      diagnoseFollowUp: !!currentCase.diagnose?.followUp,
      followUpData: !!currentCase.followUpData,
    })
    return currentCase.followUp || currentCase.diagnose?.followUp || currentCase.followUpData || {}
  })()
  const hasFollowUpAppointmentData = (followUpData.appointmentScheduled || followUpData.scheduledDate) 
    && followUpData.appointmentStatus !== 'pending'
    && !hasPendingReopen  // v121.8.0: simplified — covers both encounter-aware and legacy
    && !followUpDeclined(followUpData)  // PROD-983: a declined follow-up time is never shown as scheduled
    && !followUpCancelledByPhysician(followUpData)  // PROD-1137: nor a follow-up the physician cancelled
  // PROD-983: the physician declined the patient's held follow-up reschedule (API PROD-970).
  const followUpWasDeclined = followUpDeclined(followUpData) && !hasPendingReopen
  // ★ PROD-1137: the physician (or his MA) cancelled the confirmed follow-up visit. Its line says so and
  //   offers a new time through the existing follow-up scheduling screen.
  const followUpPhysicianCancel = hasPendingReopen ? null : physicianCancelOfFollowUp(followUpData)

  // v121.6.3: Grey initial appointment when its encounter is finalized (visit is in the past)
  // This is the general rule: an appointment greys when the MD finalizes that encounter
  const initialEncounter = (currentCase.encounters || []).find(e => (e.encounter_number || e.encounterNumber) === 1)
  const isInitialFinalized = initialEncounter?.finalized === true
  // v121.6.5: Grey initial appointment when encounter is finalized, OR case has progressed past initial
  // Fallback: if case is in_follow_up/completed and encounterNum >= 2, initial is inherently in the past
  const shouldGreyInitialAppointment = isInitialFinalized || (isFollowUpEncounter && ['completed', 'in_follow_up', 'in_observation'].includes(currentCase.status))
  
  // Debug logging
  console.log('[CaseHome] followUpData:', { scheduled: !!followUpData.scheduledDate, appointmentStatus: followUpData.appointmentStatus ?? null }) // PROD-903: no whole object
  console.log('[CaseHome] hasFollowUpAppointmentData:', hasFollowUpAppointmentData)
  // MEET PA5: presence, status and ids only — the appointment rows carry meeting_url on a Meet visit.
  console.log('[CaseHome] Appointment data:', {
    hasAppointmentRequest: !!currentCase.appointmentRequest,
    appointmentStatus: currentCase.appointmentStatus,
    appointmentId: currentCase.appointment?.id ?? null,
    scheduledAppointmentId: currentCase.scheduledAppointment?.id ?? null,
    appointmentIds: allAppointments.map((a) => a?.id),
    hasFollowUp: !!currentCase.followUp,
    hasFollowUpAppointmentData,
    originalAppointmentId: originalAppointment?.id ?? null
  })
  
  // Format original appointment for display
  // ★ PROD-1137: the physician (or his MA) cancelled the confirmed initial visit. The API removed the
  //   confirmed* fields and kept the cancelled slot; the line names that slot, not the old request.
  const initialPhysicianCancel = physicianCancelOfRequest(originalAppointment)
  // s126 (TestFlight build 92 note 11): the initial appointment's own row is listed once, as the initial line —
  //   not a second time among the completed visits.
  const completedVisitRows = completedAppointments.filter(a => !(originalAppointment?.id && a.id === originalAppointment.id))
  const formattedOriginalAppointment = originalAppointment ? {
    date: (() => {
      const dateStr = initialPhysicianCancel?.date || originalAppointment.confirmedDate || originalAppointment.requestedDate
      if (!dateStr) return null
      return formatDate(dateStr, { weekday: 'short', month: 'short', day: 'numeric' })
    })(),
    time: (() => {
      const timeStr = initialPhysicianCancel?.time || originalAppointment.confirmedTime || originalAppointment.requestedTime
      if (!timeStr) return null
      return formatAppointmentTime(timeStr)
    })(),
    visitType: (originalAppointment.confirmedType || originalAppointment.appointmentType) === 'video' ? 'telehealth' : 'in-person',
    type: 'initial',
    status: originalAppointment.status || 'pending'
  } : null
  
  // ★ P20 (Q28): while a reschedule is pending the "Your visit" line keeps the BOOKED visit (the held
  //   entry of status_history[-1]) and a second line says what was requested. The requested time is the
  //   row's own date / time, formatted by the same formatDate / formatAppointmentTime as the booked line.
  //   Under es the key is withheld (src/i18n/pendingEs.js) and today's screen stays (hasText).
  const heldChange = (() => {
    if (!originalAppointment || !hasText('caseHome.changeRequested')) return null
    if (requestDeclined(currentCase.appointmentRequest)) return null
    const row = rowsOfCurrentPhysician(currentCase, allAppointments).find(a => a.id === originalAppointment.id)
    const change = requestedChangeOf(row)
    if (!change) return null
    return {
      booked: {
        date: formatDate(change.booked.date, { weekday: 'short', month: 'short', day: 'numeric' }),
        time: formatAppointmentTime(change.booked.time),
        visitType: (change.booked.type === 'video' || change.booked.type === 'telehealth') ? 'telehealth' : 'in-person',
      },
      requested: {
        date: formatDate(change.requested.date, { weekday: 'short', month: 'short', day: 'numeric' }),
        time: formatAppointmentTime(change.requested.time),
      },
    }
  })()

  // Debug appointment status
  if (originalAppointment) {
    console.log('[CaseHome] Original appointment:', {
      status: originalAppointment.status,
      hasConfirmedDate: !!originalAppointment.confirmedDate,
      hasRequestedDate: !!originalAppointment.requestedDate,
      formattedStatus: formattedOriginalAppointment?.status
    })
  }
  
  // Format follow-up appointment for display  
  const formattedFollowUpAppointment = hasFollowUpAppointmentData ? {
    date: followUpData.scheduledDate ? formatDate(followUpData.scheduledDate, {
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    }) : null,
    time: followUpData.scheduledTime ? formatAppointmentTime(followUpData.scheduledTime) : null,
    visitType: ['video', 'virtual', 'telehealth'].includes(followUpData.visitType) ? 'telehealth' : 'in-person',
    type: 'follow-up'
  } : null


  // Handle back navigation with warning for incomplete cases
  const returnTo = safePath(location.state?.returnTo, '/home')

  // ★ v242.235.0 (session 96, Dr. Rami's mockup-B ruling): "the case should save only after the
  //   health questionnaire is completed and describe section begins" + leaving keeps a draft,
  //   silently. Back no longer stops the patient with Save-as-draft / Delete: the case already
  //   exists (created when the screening completes) and every Describe step saves as it goes.
  //   Deleting stays an explicit act (the trash button / case menu), so the Exit Warning modal
  //   below is no longer opened from Back.
  const handleBack = () => {
    navigate(returnTo)
  }
  
  // Save as draft and exit
  const handleSaveAsDraft = () => {
    setShowExitWarning(false)
    navigate('/cases')
  }
  
  // Confirm exit and delete incomplete case
  const handleConfirmExit = async () => {
    if (currentCase?.id) {
      console.log('[CaseHome] Deleting incomplete case:', currentCase.id)
      // ★ PROD-416: a delete the server refuses leaves the case; the patient stays and is told.
      try { await deleteCase(currentCase.id) } catch (err) { reportDeleteFailure(t, err); return }
    } else {
      clearCurrentCase()
    }
    setShowExitWarning(false)
    navigate(returnTo)
  }

  // v125.2.1: Cancel pending observation bridge — revert to observation
  const handleCancelBridge = async () => {
    if (cancellingBridge || !currentCase?.id) return
    setCancellingBridge(true)
    try {
      // v242.170.0 — was `|| 2`, a typed constant wearing a default.
      const encNum = currentEncounterNumber(currentCase)
      const { encountersAPI } = await import('../services/api')
      const result = await encountersAPI.cancelAppointmentRequest(currentCase.id, encNum)
      console.log('[CaseHome] Follow-up request cancelled:', !!result?.success) // PROD-903: no whole answer

      // ★★★ D332(l) — THIS USED TO CLAIM A REVERT THAT NEVER HAPPENED. It optimistically
      //   set the case back to `in_observation` and rewrote `followUp.type`, then called
      //   loadCases() — so the patient watched their case return to observation and then
      //   silently become a follow-up again on the refresh. The server had skipped the
      //   revert for two versions; only this block made it look otherwise.
      // ★★ THE RULING: cancelling withdraws a TIME, not a symptom report. The concern
      //   converted the case and that stands; only the request is cancelled.
      updateCase({
        appointmentRequest: {
          ...currentCase.appointmentRequest,
          status: 'cancelled',
        },
      })
      
      // Refresh from API
      await loadCases()
      setShowCancelBridge(false)
    } catch (err) {
      console.error('[CaseHome] Cancel bridge failed:', err)
      alert(t('errors2.failedToCancel'))
    }
    setCancellingBridge(false)
  }

  // ★★ PROD-644 (patient P2): the reschedule offers only the modalities the case's physician OFFERS
  //   for that visit kind (utils/offeredModalities, the md10a rule). When the doctor record does not
  //   say, both, as before.
  const rescheduleOfferedFor = (type) => allowedVisitTypesForDoctor({ doctor: caseDoctor(currentCase), category: type === 'initial' ? 'initial' : 'followUp', videoAllowedForCategory: true }).types

  // v138.3.0: Open reschedule modal and fetch available slots
  const openRescheduleModal = async (type = 'initial') => {
    setRescheduleType(type)
    setRescheduleDate(null)
    setRescheduleTime(null)
    setRescheduleComplete(false)
    setRescheduleError(null)
    setRescheduleSubmitting(false)
    // Default visit type from current appointment
    const currentApptType = type === 'initial' 
      ? (originalAppointment?.appointmentType || originalAppointment?.confirmedType || 'in-person')
      : (followUpData?.visitType || 'in-person')
    // PROD-644: the default is the current visit's modality when the physician offers it, else the one they offer.
    const offered = rescheduleOfferedFor(type)
    const wantVideo = ['video', 'telehealth', 'virtual'].includes(currentApptType)
    setRescheduleVisitType(offered.includes(wantVideo ? 'video' : 'in_person') ? (wantVideo ? 'video' : 'in-person') : (offered.includes('video') ? 'video' : 'in-person'))
    setShowRescheduleModal(true)
    setRescheduleSlotsLoading(true)
    
    // ★★★ v242.87.0 (TF b34 #11) — four causes used to collapse into one
    //   empty state. They are kept apart now, and `rescheduleNoSlotsReason`
    //   carries which one it was.
    setRescheduleNoSlotsReason(null)
    // ★★ TEST 1.35 — the causes are now classified in ONE place (utils/bookableSlots.js),
    //   the same rule every booking screen uses:
    //     'no-doctor'      a case shared by CODE names nobody: no calendar, no office
    //     'scheduling-off' online scheduling is off: calling the office IS the instruction
    //     'no-hours'       on with no days (API reason no_hours): same copy as off
    //     'none-open'      genuinely nothing open in the next 30 days
    //     'fetch-failed'   the read FAILED — not an absence of appointments. api.js used
    //                      to fold this into enabled:false, so it reached the patient as
    //                      "no availability"; it now carries fetchFailed and reaches the
    //                      existing Try again affordance.
    let doctorsAPI = null
    try { ({ doctorsAPI } = await import('../services/api')) } catch (err) {
      console.warn('[CaseHome] Failed to load the slots reader:', err)
    }
    const doctorId = currentCase?.sentToDoctor || currentCase?.doctor?.id
    const apiCategory = type === 'initial' ? 'initial' : 'follow_up'
    const { slots, reason, places } = doctorsAPI || !doctorId
      ? await bookableSlots(doctorsAPI, doctorId, 30, apiCategory)
      : { slots: [], reason: 'fetch-failed' }
    setRescheduleSlots(slots)
    setReschedulePlaces(places || null)
    setRescheduleNoSlotsReason(reason)
    if (reason === 'fetch-failed') {
      setRescheduleError(t('caseHome.slotsFetchFailed', "We couldn't load available times just now."))
    }
    setRescheduleSlotsLoading(false)
  }

  // v138.3.0: Submit reschedule
  const handleRescheduleSubmit = async () => {
    if (!rescheduleDate || !rescheduleTime || rescheduleSubmitting) return
    setRescheduleSubmitting(true)
    setRescheduleError(null)

    try {
      const { casesAPI, appointmentsAPI } = await import('../services/api')
      const appointmentType = rescheduleVisitType === 'video' ? 'video' : 'in_person'

      if (rescheduleType === 'initial') {
        // ★★★ PROD-192 — THE RESCHEDULE ROUTE OWNS appointment_request. This branch used
        //   to PUT { ...local appointmentRequest, new date, status } to /cases/:id FIRST
        //   and only then update the appointment row — so the client's copy (and a
        //   'pending' it could invent when the local request had no confirmedDate) went
        //   over the server's, and PUT /appointments/:id then read that back as truth.
        //   PUT /appointments/:id (update_appointment) already syncs the initial
        //   request itself: requested fields, pending vs auto-accept confirm, clearing
        //   stale confirmed fields. So when the row exists the client calls only that
        //   route and mirrors the server's result locally (updateCase, no API).
        //   Only when there is NO appointment row is the client PUT the only writer; it
        //   is then merged onto a FRESH read of the server's request and never carries
        //   a status (mergeOntoServerRequest drops it).
        //   Gate: scripts/check-appointment-request-owner.mjs.
        const serverRequest = await readServerAppointmentRequest(currentCase.id)
        const isPending = (serverRequest?.status || 'pending') === 'pending' || !serverRequest?.confirmedDate

        const allAppts = await appointmentsAPI.getAll()
        // v225.16.0 (C-A-175): exclude follow-up rows so an initial reschedule never
        // lands on the case's follow-up appointment.
        const matchingAppt = allAppts.find(a => a.case_id === currentCase.id && a.status !== 'cancelled' && a.appointment_category !== 'follow_up' && !isOutsideAppointment(a))
        if (matchingAppt) {
          // A failure here (409 slot conflict included) now reaches the catch below:
          // nothing was rescheduled, so the patient must not be told it was.
          await appointmentsAPI.update(matchingAppt.id, {
            date: rescheduleDate,
            time: rescheduleTime,
            type: rescheduleVisitType === 'video' ? 'video' : 'in_person',
            // v180.1.0: Preserve pending status — don't set to 'rescheduled' if MD hasn't accepted
            status: isPending ? 'pending' : 'rescheduled',
            scheduled_by: 'patient',
          })
        } else if (serverRequest) {
          // No appointment row: the case PUT is the only writer. Merge on the fresh read.
          // ★★ Delta review S1 (PROD-918 follow-up): this PUT (update_case) runs no slot-conflict
          //   check and tells the physician nothing, and the merge keeps the server's status. It is
          //   allowed ONLY while the physician has not acted on the request ('pending', or no
          //   status, which the API's update_appointment also reads as pending): then the changed
          //   requested time is simply what the physician will review. Any other status
          //   (cancelled, rejected, confirmed / modified with no row, requested whose row is gone,
          //   finalized) would leave nothing moved under a "Rescheduled" screen, so nothing is
          //   written and the patient reads the same failure as before.
          //   Gate: scripts/check-case-get-shape.mjs CG-4 / CG-10.
          // ★ PROD-1137: a visit the PHYSICIAN cancelled is the one other request that may move on: the
          //   patient chooses a new time with the same physician, and initial-<case> books it as a new
          //   pending request (the API auto-confirms it when the physician auto-accepts). A 'cancelled'
          //   without the physician's mark (the patient's own, the system's) is still refused below.
          //   Gate: scripts/check-case-get-shape.mjs CG-11.
          const serverStatus = requestCancelledByPhysician(serverRequest) ? 'pending' : String(serverRequest.status ?? '').trim().toLowerCase()
          if (serverStatus !== 'pending' && serverStatus !== '') throw new Error(t('errors2.failedToReschedule'))
          // ★★ PROD-925 (patient half): the pending request with no row is moved through the
          //   appointment route, not the generic case PUT. PUT /appointments/initial-<case>
          //   restores the share's pending row, runs the double-booking check and the patient
          //   refusals, syncs the request itself and notifies the physician. A refusal (409
          //   slot conflict, 400/404) reaches the catch below with the existing texts.
          //   Gate: scripts/check-reschedule-pending-no-row.mjs.
          await appointmentsAPI.update('initial-' + currentCase.id, {
            date: rescheduleDate,
            time: rescheduleTime,
            type: rescheduleVisitType === 'video' ? 'video' : 'in_person',
            status: 'pending',
            scheduled_by: 'patient',
          })
        } else {
          // Neither an appointment row nor a server request: there is nothing to move,
          // and inventing a request here is exactly the defect.
          throw new Error(t('errors2.failedToReschedule'))
        }

        // LOCAL mirror only: whatever the server now holds.
        try {
          const after = await readServerAppointmentRequest(currentCase.id)
          if (after) updateCase({ appointmentRequest: after })
        } catch (readErr) {
          console.warn('[CaseHome] appointment_request read-back failed (non-fatal):', readErr?.message || readErr)
        }
        
        console.log('[CaseHome] Initial appointment rescheduled', isPending ? '(still pending)' : '(rescheduled)')
      } else {
        // Follow-up: resolve the REAL existing follow-up appointment before updating.
        // v225.16.0 (C-A-175): the old `followup-${id}` fabricated id always failed the
        // update and fell through to create() — spawning a duplicate appointment each
        // reschedule. Look up the actual follow-up row and update it; create only if none.
        let followUpApptId = currentCase.followUpAppointment?.id || null
        try {
          if (!followUpApptId) {
            const allAppts = await appointmentsAPI.getAll()
            const match = allAppts.find(a => a.case_id === currentCase.id && a.appointment_category === 'follow_up' && a.status !== 'cancelled' && !isOutsideAppointment(a))
            if (match) followUpApptId = match.id
          }
          if (followUpApptId) {
            await appointmentsAPI.update(followUpApptId, {
              date: rescheduleDate,
              time: rescheduleTime,
              type: appointmentType,
              status: 'rescheduled',
              scheduled_by: 'patient',
            })
          } else {
            // ★★ PROD-925 Q1 (patient half): NO appointment row. A move of the follow-up goes through
            //   the appointment route, like a move with a row: PUT /appointments/followup-<case>
            //   restores the row, runs the slot / hours / modality refusals, applies the physician's
            //   hold (pending_reschedule in manual review) and notifies him. create() bypasses the
            //   hold. A refusal (409) reaches the catch below with the existing texts.
            //   Release order: needs API E3 part B live. An API without it answers 404 or a 200
            //   that inserted no row (no real row id): then the old create below still runs.
            //   Gate: scripts/check-reschedule-pending-no-row.mjs (arms Q5-Q7).
            // MEET PA0: the recording question comes BEFORE any write, so closing the sheet writes
            //   nothing (the update below already moves the follow_up JSON on an API without E3 part B).
            //   Only a video visit asks, and only once MEET_RECORDING_RELEASED is true.
            const consent = await askRecordingConsent(appointmentType)
            if (!consent.proceed) { setRescheduleSubmitting(false); return }
            let movedRow = null
            try {
              const moved = await appointmentsAPI.update('followup-' + currentCase.id, {
                date: rescheduleDate,
                time: rescheduleTime,
                type: appointmentType,
                status: 'rescheduled',
                scheduled_by: 'patient',
              })
              movedRow = isUuid(moved?.appointment?.id) ? moved.appointment : null
            } catch (moveErr) {
              if (appointmentRefused(moveErr)) throw moveErr
              if (moveErr?.status !== 404) throw moveErr
            }
            if (!movedRow) {
              // Fallback for an API without E3 part B: no row was restored, so create one as before.
              // The consent asked above travels with the NEW row; a moved row records no decision
              //   (API M3), the pre-join sheet (PA1) asks.
              await appointmentsAPI.create({
                case_id: currentCase.id,
                date: rescheduleDate,
                time: rescheduleTime,
                type: appointmentType,
                appointment_category: 'follow_up',
                status: 'rescheduled',
                scheduled_by: 'patient',
                ...consent.fields, // { recordingConsent, consentVersion } or {}
              })
            }
          }
        } catch (fuErr) {
          // ★★★ P1 round 5 (M1) — A REFUSAL IS NOT A SYNC FAILURE. This catch swallowed a
          //   409 doctor_has_no_hours or a slot conflict and fell through to the follow_up
          //   write and "Rescheduled" below, so the case said the visit moved to a time the
          //   physician had refused. A refusal goes to the catch below, which shows it.
          if (appointmentRefused(fuErr)) throw fuErr
          console.warn('[CaseHome] Follow-up reschedule appointment sync failed:', fuErr)
        }
        // Update local follow-up data
        const updatedFollowUp = {
          ...(currentCase.followUp || {}),
          scheduledDate: rescheduleDate,
          scheduledTime: rescheduleTime,
          visitType: rescheduleVisitType === 'video' ? 'telehealth' : 'in-person',
          modifiedAt: new Date().toISOString(),
          modifiedBy: 'patient',
          appointmentStatus: 'rescheduled',
        }
        await casesAPI.update(currentCase.id, { follow_up: updatedFollowUp })
        updateCase({ followUp: updatedFollowUp })
        console.log('[CaseHome] Follow-up rescheduled')
      }

      setRescheduleComplete(true)
      // Refresh case data after a brief delay
      setTimeout(() => loadCases().catch(() => {}), 800)
    } catch (err) {
      console.error('[CaseHome] Reschedule failed:', err)
      // P1 round 5 (M1): a contract refusal reads its own message (doctor_has_no_hours,
      //   slot_conflict, ...), not "Failed to reschedule"; a bare 409 conflict is "just booked".
      const refusalKey = appointmentErrorKey(err) || ((err?.status === 409 || err?.conflict === true) ? 'errors2.timeSlotBooked' : null)
      setRescheduleError(refusalKey ? t(refusalKey) : (err.message || t('errors2.failedToReschedule')))
    }
    setRescheduleSubmitting(false)
  }

  // v242.71.0 — assembled once, in component scope. It used to live inline on a
  // modal mount at the bottom of the file, which is why the screen could grow a
  // second narrative engine without anyone noticing the first one.
  // ★ PROD-1079: the note / exam the agent is given come from a signed, not-held visit only
  //   (utils/signedVisit.js); md's unsigned draft and a held note never reach the prompt.
  const agentVisit = agentVisitContext(currentCase)
  const agentCaseContextBase = {
          // Basic info
          specialty: currentCase?.specialtyId ? formatSpecialtyName(currentCase.specialtyId, t) : '',
          focusArea: currentCase?.focusAreaId || currentCase?.focusArea || '',
          phase: chatPhase,
          status: currentCase?.status || 'draft',
          deliverState: currentCase?.deliverState || '',
          // v235.16.0 — temporal awareness for the opening greeting
          checkInEligible: agentTemporal.checkInEligible,
          appointmentLabel: agentTemporal.appointmentLabel,
          // External doctor path info (A-3)
          doctorSource: currentCase?.doctorSource || currentCase?.doctor_source || '',
          isExternalPath: isExternalPath,
          externalStatus: isExternalPath ? (
            currentCase?.externalDoctorNote ? 'uploaded' : 
            currentCase?.visitPrepCode ? 'prep_ready' : 'preparing'
          ) : '',
          // Describe phase data
          voiceSummary: currentCase?.describe?.voiceTranscript || currentCase?.voiceRecording?.transcript || '',
          voiceTextInput: currentCase?.voiceTextInput || currentCase?.describe?.voiceTextInput || '',
          questionnaireAnswers: currentCase?.answers || {},
          // Doctor + appointment
          doctorName: currentCase?.sentToDoctorName || (currentCase?.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase)) : ''),
          hasAppointment: !!(currentCase?.appointmentRequest?.status === 'confirmed'),
          appointmentDate: currentCase?.appointmentRequest?.scheduledDate || currentCase?.appointment?.date || '',
          // Diagnose phase data (populated after physician finalizes)
          diagnosis: currentCase?.diagnose?.diagnosis || currentCase?.diagnosis || '',
          treatmentPlan: currentCase?.diagnose?.treatmentPlan || '',
          avsContent: currentCase?.avsContent || currentCase?.afterVisitSummary || currentCase?.diagnose?.avsContent || '',
          consultNote: agentVisit.consultNote,
          examinationNotes: agentVisit.examinationNotes,
          // AI analysis (patient has seen this — from Describe phase)
          patientNarrative: currentCase?.patientSummary?.patientNarrative || '',
          aiDifferential: (currentCase?.patientSummary?.differentialDx || []).map(d => d?.diagnosis).filter(Boolean),
          // Follow-up
          followUp: planFollowUp(currentCase), // PROD-1079: md's draft plan keys dropped until signed
          // Language + Profile
          language: language,
          profileSummary: buildProfileSummary(profile),
  }

  // ★★★ v242.87.0 (TF b34 #7) — `overflow-auto` removed from the root div
  //   below, and that is HALF the fix for the sticky header; neither half works
  //   alone. This div declared itself a scroll container, but its parent
  //   (PageWrapper, min-height:100%, height auto) has no definite height, so
  //   `min-h-full` resolved to nothing and the div simply grew to its content.
  //   It was a scrollport that never scrolled — the real scrolling ancestor is
  //   MobileLayout.jsx:61. A `sticky` header inside a dead scrollport is
  //   positioned against the dead scrollport and translates with the page
  //   exactly as an unstuck header does. Remove the overflow first and the
  //   header's sticky resolves against the container that actually moves.
  // ★★ AND THIS COMMENT IS A `//` COMMENT FOR A REASON. Written as
  //   `{/* … */}` immediately after `return (` it is a JSX comment in
  //   root-SIBLING position, which does not parse — ORELLA_RULES §0(3). I made
  //   that exact mistake twice in one session, in two different files, and
  //   check-parse.mjs caught it both times. Nothing else did.
  // s124 (canvas "Orella Case Home — Revised" A–C): the status card's facts, and the live step's action, which moves
  //   from the step list into the card (the list keeps one row per step, tappable, no button of its own).
  const stageActions = {
    describe: t('caseHome.stepActionDescribe', 'Continue'),
    detect: t('caseHome.stepActionDetect', 'Add photos & documents'),
    review: t('caseHome.stepActionReview', 'Review your diagnosis'),
    deliver: t('caseHome.stepActionDeliver', 'Open your care plan'),
  }
  const statusCard = caseStatusModel({
    sections, casePhase, status, codeShared: isCodeSharedUnclaimed(currentCase), external: isExternalPath,
    isFollowUp: isFollowUpEncounter, diagnoseAcknowledged, deliverState: currentCase.deliverState, shareInline: canShare,
  })
  const cardSection = spineLayout === 'pager' && statusCard.actionIndex >= 0 ? sections[statusCard.actionIndex] : null
  // PROD-1422 (Alex 2026-11-03): a share code no doctor has claimed ("Shared - code active") is not a doctor. Until
  //   someone claims the case, the card also offers "Find a doctor" -> the same Orella search the share hub's button
  //   opens when no office send is live (/case/share {findDoctor}). A case faxed to an office ("Sent to" in Your
  //   visit) is shared with a doctor; its switch stays on the hub (D476 one live share), so the button hides there.
  //   officeSend === null: the fax sends were READ and none is live (undefined = not known yet -> hidden).
  const showFindDoctor = isCodeSharedUnclaimed(currentCase) && officeSend === null && currentCase.status !== 'completed'
  const cardActionLabel = !cardSection ? null
    : cardSection.externalUpload ? t('caseHome.uploadDoctorNote', 'Upload notes')
    : cardSection.needsReview ? stageActions.review
    : (stageActions[cardSection.id] || t('common.open', 'Open'))
  return (
    <div className="min-h-full bg-slate-100 flex flex-col">
      {/* Exit Warning Modal */}
      {showExitWarning && (
        <Modal onClose={() => setShowExitWarning(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <button onClick={() => setShowExitWarning(false)} className="text-gray-400">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('caseHome.incompleteCase')}</h3>
            <p className="text-gray-600 mb-6">{t('caseHome.incompleteWarning')}</p>
            <div className="space-y-3">
              <button
                onClick={handleSaveAsDraft}
                className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold"
              >
                {t('caseHome.saveAsDraft')}
              </button>
              {/* ★ PROD-416: no delete on a draft a physician holds */}
              {!isHeldByDoctor(currentCase) && (
              <button
                onClick={handleConfirmExit}
                className="w-full py-3 border border-red-300 text-red-500 rounded-full font-semibold"
              >
                {t('caseHome.deleteAndLeave')}
              </button>
              )}
              <button
                onClick={() => setShowExitWarning(false)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
              >
                {t('caseHome.continueWorking')}
              </button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* ============ COMPACT NAVY HEADER (Option B) ============ */}
      <header 
        className="text-white flex-shrink-0 relative overflow-hidden sticky top-0 z-30"
        data-case-header="1"
        /* v242.77.0 (TF-27) — the case number was rendering UNDER the status
           bar clock on a 6.1" device. The mockup's 6px top padding is measured
           from the top of a phone FRAME in a browser, which has no status bar;
           on hardware the inset only reserves the notch, and the first text row
           needs clearance below it. 14px, which is what this header had before
           the v5 port. */
        style={{ backgroundColor: NAVY, padding: '6px 16px 14px',
                 paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)' }}
      >
        {/* bokeh circles — matches Doctors / NavyPageHeader pattern (v242.17.0) */}
        <div className="absolute rounded-full" style={{ top: -30, right: 30, width: 120, height: 120, background: '#3E6E97', opacity: 0.22 }} />
        <div className="absolute rounded-full" style={{ top: 38, right: -24, width: 70, height: 70, background: '#7FB4DC', opacity: 0.14 }} />
        <div className="absolute rounded-full" style={{ top: 10, left: 130, width: 28, height: 28, background: '#A7D9FC', opacity: 0.16 }} />
        <div className="absolute rounded-full" style={{ bottom: 14, right: 90, width: 16, height: 16, background: '#A7D9FC', opacity: 0.20 }} />
        <div className="absolute rounded-full" style={{ bottom: -12, left: 44, width: 46, height: 46, background: '#3E6E97', opacity: 0.16 }} />

        <div className="relative z-10">
        {/* v242.70.0 — header ported to mockup v5. Four stacked rows became three:
            one control row, the case number, and ONE 12px breadcrumb carrying
            specialty, focus and start date together. The old layout spent 14px
            semibold on the breadcrumb and a second row with its own calendar
            icon on a date nobody acts on, which pushed the chat card and the 4D
            pager — the two things the patient came for — further down the fold. */}

        {/* Row 1: back · wordmark · status · bell */}
        <div className="flex items-center" style={{ gap: 12, height: 30, marginBottom: 11 }}>
          <button onClick={handleBack} className="p-0 border-none bg-transparent flex text-white cursor-pointer"
                  style={{ opacity: 0.9 }} aria-label={t('common.back', 'Back')}>
            <ArrowLeft size={20} />
          </button>
          <img src={orellaLogoWhite} alt="Orella" style={{ height: 19, width: 'auto', display: 'block' }} />
          {/* s124: the status moved into the Orella card below (CaseStatusBlock), where it says what happens next. */}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <NotificationBellButton variant="compact" tone="dark" />
          </span>
        </div>

        {/* s124 (Dr. Rami, build 91: "Lets make the subtext the main title and case number subtitle. Needs to be obvious
            at the top what the case is about"; canvas "Orella Case Home — Revised" A): the area over the title, the
            focus as the title, the case number and start date as the subtitle. */}
        <div data-case-area="" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: '#A7D9FC', textTransform: 'uppercase' }}>
          {formatSpecialtyName(currentCase.specialtyName, t)}
        </div>
        <h1 data-case-title="" style={{ fontWeight: 800, fontSize: 26, lineHeight: 1.15, margin: '2px 0 4px' }}>
          {currentCase.focusAreaName ? tfa(currentCase.focusAreaName) : formatSpecialtyName(currentCase.specialtyName, t)}
        </h1>
        <div data-case-subtitle="" style={{ fontSize: 13, color: '#C9E6FB', marginBottom: spineLayout === 'pager' ? 0 : 14 }}>
          {t('caseHome.case', 'Case')} {currentCase.caseNumber?.startsWith('#') ? currentCase.caseNumber : `#${currentCase.caseNumber || currentCase.id?.slice(-8).toUpperCase()}`}
          {' · '}
          {t('caseHome.startedOn', 'Started')} {currentCase.createdAt ? formatDate(currentCase.createdAt) : t('caseHome.recently')}
        </div>

        {/* The 4-segment bar moved INTO the spine card as StagePager's `segs` row,
            where it sits directly above the card it indexes. It stays here for the
            ?spine=timeline escape hatch, which has no segs of its own — removing it
            unconditionally would leave that layout with no progress indicator. */}
        {spineLayout === 'timeline' && (
          <ProgressBar
            describeComplete={describeComplete}
            detectComplete={detectComplete}
            diagnoseComplete={isCompleted && diagnoseAcknowledged}
            deliverComplete={currentCase.deliverState === 'all-done' || currentCase.deliverState === 'complete'}
            deliverUnlocked={deliverUnlocked}
          />
        )}
        </div>
      </header>

      {/* PROD-1395: someone opened this case's share code and waits for the patient's answer. The same card as the
          share hub's, directly under the header; AllowAccessScreen returns here and the read finds nothing pending. */}
      {openReq ? (
        <button type="button" data-open-request-home=""
                onClick={() => navigate(`/case/${currentCase.id}/allow-access`)}
                className="mx-4 mt-3 text-left rounded-2xl border-[1.5px] border-[#D6ECFB] bg-[#EFF8FF] px-4 py-3 flex items-center gap-3 active:opacity-80">
          <span className="flex-1 min-w-0">
            <b className="block text-[15px]" style={{ color: NAVY }}>{t('shareApproval.allowTitle')}</b>
            <span className="block text-[13px] mt-0.5 text-slate-700">{t('shareApproval.wantsToOpen', openReq.clinicianTitle ? clinicianForms(clinicianFromText(openReq.clinicianTitle)) : clinicianForms({ name: t('caseHome.aDoctor') }))}</span>
          </span>
          <ChevronRight size={18} className="flex-shrink-0" style={{ color: NAVY }} />
        </button>
      ) : null}

      {/* Orella Chat — Contextual per phase */}
      {(() => {
        // Determine chat message based on 6 case phases
        let chatMsg = ''
        let showSteps = false

        // ============ ENCOUNTER 2+ CHAT MESSAGES (v120.9.5 PA-M1) ============
        if (isFollowUpEncounter && deliverGatedSubscription) {
          // v234.15.1: MD-agnostic follow-up — Deliver is locked (no Orella MD). Don't tell the
          // patient to "check Deliver"; offer visit-summary prep via Ask Orella instead.
          chatMsg = t('caseHome.chatExternalPrep', "I've gone through your visit summary. Want to get ready for your next appointment? I can help you understand it, figure out what to ask, and what to clarify — just tap Ask Orella.")
        } else if (isFollowUpEncounter) {
          // v135.1.0: Use the already-computed row from resolveActiveEncounter (activeEncRow)
          // v124.8.0: Fall back to case status for observation detection (encounters may not be loaded from list endpoint)
          // v125.2.0: After observation→follow_up bridge, encounters array may still have stale 'observation' category.
          // If case status is in_follow_up and appointmentRequest has observationFinding, it's bridged — treat as follow_up.
          const rawEncCat = activeEncRow.category || activeEncRow.appointment_category 
            || (currentCase.status === 'in_observation' ? 'observation' : 'follow_up')
          const encCat = (rawEncCat === 'observation' && currentCase.status === 'in_follow_up' && currentCase.appointmentRequest?.observationFinding)
            ? 'follow_up' : rawEncCat
          const hasReferrals = (currentCase.referrals || []).length > 0
          
          if (encCat === 'observation') {
            // Observation encounter messages — v135.4.0: full phase coverage
            switch (encounterPhase) {
              case 'deliver_active':
                chatMsg = t('caseHome.chatObservationMonitor')
                break
              case 'deliver_submitted':
                chatMsg = t('caseHome.chatObservationSubmitted')
                break
              case 'diagnose_pending':
                chatMsg = t('caseHome.chatObservationReviewing')
                break
              case 'diagnose_active':
                chatMsg = t('caseHome.chatObservationReviewed')
                break
              case 'diagnose_acknowledged':
                chatMsg = t('caseHome.chatObservationCarePlan')
                break
              case 'complete':
                chatMsg = t('caseHome.chatObservationComplete')
                break
              default:
                chatMsg = t('caseHome.chatObservationMonitoring')
            }
          } else if (encCat === 'complete') {
            // Terminal encounter messages — v135.4.0: phase-specific
            if (encounterPhase === 'deliver_active') {
              chatMsg = t('caseHome.chatCareWrappedUp')
            } else {
              chatMsg = t('caseHome.chatCareComplete')
            }
          } else {
            // Follow-up encounter messages (default)
            switch (encounterPhase) {
              case 'deliver_active':
                if (currentCase.appointmentRequest?.status === 'pending') {
                  chatMsg = t('caseHome.chatFollowUpPending')
                } else if (isConfirmedReopen) {
                  chatMsg = t('caseHome.chatFollowUpConfirmed')
                } else {
                  chatMsg = t('caseHome.chatFollowUpPreVisit')
                }
                break
              case 'deliver_submitted':
                chatMsg = t('caseHome.chatFollowUpResponsesSent')
                break
              case 'diagnose_pending':
                // v126.6.0: Three states for diagnose_pending:
                // 1. Post-confirmation: appointment confirmed, waiting for MD to conduct visit
                // 2. Observation bridge: finding reported, waiting for MD to confirm appointment
                // 3. Standard: pre-visit submitted, waiting for MD analysis
                if (currentCase.appointmentRequest?.status === 'confirmed' || currentCase.appointmentRequest?.status === 'scheduled') {
                  chatMsg = t('caseHome.chatFollowUpConfirmed')
                } else if (currentCase.appointmentRequest?.observationFinding) {
                  chatMsg = t('caseHome.chatFollowUpRequestSent')
                } else {
                  chatMsg = t('caseHome.chatFollowUpScheduled')
                }
                break
              case 'diagnose_active':
                chatMsg = t('caseHome.chatFollowUpAnalysis')
                break
              case 'diagnose_acknowledged':
                chatMsg = t('caseHome.chatFollowUpCarePlan')
                break
              case 'complete':
                chatMsg = t('caseHome.chatFollowUpCaseComplete')
                break
              default:
                chatMsg = t('caseHome.chatFollowUpStatus')
            }
          }
          
          // Append referral note if active
          if (hasReferrals && encCat !== 'complete') {
            const refSpecialty = currentCase.referrals[0]?.specialty || 'a specialist'
            chatMsg += ` Also, your doctor has referred you to ${refSpecialty} — you'll find the details in Deliver.`
          }
        } else {
        // ============ STANDARD ENCOUNTER 1 CHAT ============
        // v184.0: Check-in eligibility — 24hr window before appointment
        const checkInEligible = isCheckInEligible(status, currentCase.appointmentRequest, new Date(), currentCase) // PROD-1007

        switch (casePhase) {
          case 1: // Getting Started — brand new, Describe not yet started
            chatMsg = t('caseHome.chatPhase1') || "Let's get started. Tap Describe when you're ready — it only takes a few minutes."
            showSteps = true
            break
          case 2: // Complete Your Case — describe started but not submitted
            chatMsg = t('caseHome.chatPhase2') || "Good start — we're almost there. Tap Describe to pick up where you left off."
            break
          case 3: // Share Case — describe done, share next (v143: before Detect)
            chatMsg = t('caseHome.chatDescribeDone')
            break
          case 4: // Shared, detect pending — status may be claimed/scheduled/checked_in
            if (status === 'checked_in') {
              chatMsg = t('caseHome.chatCheckedInDetect')
            } else if (checkInEligible && (status === 'scheduled' || status === 'claimed' || status === 'in_progress')) {
              chatMsg = t('caseHome.chatApptSoonDetect')
            } else if (status === 'scheduled') {
              // s119 D530: the API's title, else the renderer ("Jane Smith, NP" never becomes "Dr. Jane Smith, NP")
              const displayName = currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase), 'short') : '') || 'Your doctor'
              chatMsg = t('diagnose.appointmentConfirmedMsg', { doctor: displayName }) || `${displayName} has confirmed your appointment. Tap Detect to add any photos, documents, or lab results before your visit.`
            } else if (status === 'claimed' || status === 'in_progress') {
              // s119 D530: the API's title, else the renderer ("Jane Smith, NP" never becomes "Dr. Jane Smith, NP")
              const displayName = currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase), 'short') : '') || 'Your doctor'
              chatMsg = t('caseHome.chatAcceptedReviewing', { doctor: displayName })
            } else if (isCodeSharedUnclaimed(currentCase)) {
              // ★★ v242.98.0 (TF b37 #3) — its own copy, because this state is not
              //   the one chatCaseShared describes. Dr. Rami: "It has been shared
              //   but is not in an arrested state." The old line said "Your case is
              //   shared with your doctor! ... they'll be able to see them right
              //   away" — and there IS no doctor: the code is out, nobody has
              //   opened it, and "your doctor" is a person who does not exist yet.
              chatMsg = t('caseHome.chatCodeShared')
            } else {
              chatMsg = t('caseHome.chatCaseShared')
            }
            break
          case 5: // Being Reviewed — shared with doctor
            if (isExternalPath && (status === 'claimed' || status === 'in_progress')) {
              // External case claimed by Orella doctor — conversion in progress
              const drName = currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase), 'short') : t('caseHome.aDoctor'))
              chatMsg = t('diagnose.doctorClaimedMsg', { doctor: drName })
            } else if (isExternalPath) {
              // External doctor path — patient has Visit Prep doc, visiting their own provider
              if (currentCase.externalDoctorNote || currentCase.external_doctor_note) {
                chatMsg = t('caseHome.chatUploadingNotes')
              } else {
                chatMsg = t('caseHome.chatVisitPrepReady')
              }
            } else if (status === 'checked_in') {
              chatMsg = t('caseHome.chatCheckedInNotified')
            } else if (checkInEligible && (status === 'scheduled' || status === 'claimed' || status === 'in_progress')) {
              chatMsg = t('caseHome.chatApptSoon')
            } else if (status === 'scheduled') {
              // Orella path — Appointment confirmed, doctor reviewing
              // s119 D530: the API's title, else the renderer ("Jane Smith, NP" never becomes "Dr. Jane Smith, NP")
              const displayName = currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase), 'short') : '') || 'Your doctor'
              chatMsg = t('diagnose.appointmentConfirmedMsg', { doctor: displayName })
            } else if (status === 'claimed' || status === 'in_progress') {
              // Orella path — Doctor has accepted/claimed the case, appointment not yet confirmed
              // s119 D530: the API's title, else the renderer ("Jane Smith, NP" never becomes "Dr. Jane Smith, NP")
              const displayName = currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase), 'short') : '') || 'Your doctor'
              chatMsg = t('caseHome.chatAcceptedAwaitingAppt', { doctor: displayName })
            } else if (isCodeSharedUnclaimed(currentCase)) {
              // v242.98.0 (TF b37 #3) — same state, one phase later. The patient
              // has submitted Detect; the code is still unclaimed.
              chatMsg = t('caseHome.chatCodeShared')
            } else {
              // Orella path — Shared but not yet accepted by a doctor
              chatMsg = t('caseHome.chatCaseSubmitted')
            }
            break
          case 6: // Consult Completed — MD finalized, results available
            if (!diagnoseAcknowledged) {
              chatMsg = t('caseHome.chatPhase6Review') || "Your results are ready. Tap Diagnose to go through your diagnosis, care instructions, and what we're recommending next."
            } else if (deliverNeedsOrellaMD) {
              // v229.4.0: External-note case, Diagnose acknowledged, Deliver locked (no Orella MD).
              // The dialogue invites visit prep; the Ask Orella chip opens the chat that takes over —
              // helping the patient understand their summary and get ready for their next visit.
              chatMsg = t('caseHome.chatExternalPrep', "I've gone through your visit summary. Want to get ready for your next appointment? I can help you understand it, figure out what to ask, and what to clarify — just tap Ask Orella.")
            } else {
              // Deliver-state-aware messages (user has already visited Deliver)
              const ds = currentCase.deliverState
              if (ds === 'all-done') {
                chatMsg = t('caseHome.chatAllDone')
              } else if (ds === 'complete') {
                chatMsg = t('caseHome.chatInitialCareComplete')
              } else if (ds === 'scheduled') {
                chatMsg = t('caseHome.chatFollowUpScheduledDeliver')
              } else {
                // First time heading to Deliver — use encounter category (primary) or MD's disposition (fallback)
                // v154.1.2 PA-M1: Fixed — reads disposition, not modality (followUp.type is video/in_person/phone)
                const followUpDisposition = planFollowUp(currentCase).disposition // PROD-1079: signed only
                  || currentCase.diagnoseData?.followUpDisposition
                  || planFollowUp(currentCase).followUpPlan
                // For initial encounters, the activeEncCategory won't be set until acknowledge creates N+1
                // Use activeEncCategory if available (post-acknowledge), else fall back to disposition
                const planForMessage = activeEncCategory === 'follow_up' ? 'in_follow_up'
                  : activeEncCategory === 'observation' ? 'observation'
                  : activeEncCategory === 'complete' ? 'completed'
                  : followUpDisposition
                
                if (planForMessage === 'in_follow_up' || planForMessage === 'followup' || planForMessage === 'follow_up') {
                  chatMsg = t('caseHome.chatPostDiagnoseFollowUp')
                } else if (planForMessage === 'observation') {
                  chatMsg = t('caseHome.chatPostDiagnoseObservation')
                } else if (planForMessage === 'referral' || planForMessage === 'refer') {
                  chatMsg = t('caseHome.chatPostDiagnoseReferral')
                } else if (planForMessage === 'completed' || planForMessage === 'complete' || planForMessage === 'pcp') {
                  chatMsg = t('caseHome.chatPostDiagnoseComplete')
                } else {
                  chatMsg = t('caseHome.chatPostDiagnoseFallback')
                }
                // Append referral note if present on initial encounter
                const initRefs = currentCase.referrals || []
                if (initRefs.length > 0) {
                  const refSpec = initRefs[0]?.specialty || 'a specialist'
                  chatMsg += ` Your doctor has also referred you to ${refSpec} — details are in Deliver.`
                }
              }
            }
            break
          default:
            chatMsg = t('caseHome.chatPhase1') || "Let's take a look at what's going on."
        }
        } // close else (encounter 1)
        
        // The opener the store will use for a fresh thread is THIS screen's line,
        // not a second one derived from the same facts inside useAgentStore.
        const agentCaseContext = { ...agentCaseContextBase, opener: chatMsg }

        return (
          <div className="px-4 relative z-10" style={{ marginTop: 22, marginBottom: 14 }}>
            <style>{`
              @keyframes dotPulse {
                0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                40% { opacity: 1; transform: scale(1.1); }
              }
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
            `}</style>
            {/* v242.70.0 — chat card ported to mockup v5.
                The card used to end with an "Ask Orella" CTA pinned inside the
                speech bubble under a hairline divider: a button that says "you
                may now speak" instead of a place to speak. The mockup replaces
                it with a composer that LOOKS like what it opens, so the way in
                is the same shape as the thing behind it. */}
            <div ref={chatCardRef} style={{
              background: '#fff', border: '1.5px solid #A7D9FC', borderRadius: 18, padding: 13,
              boxShadow: '0 5px 16px rgba(70,168,249,0.10)',
            }}>
              <CaseStatusBlock
                model={statusCard}
                pillText={t(`caseHome.${statusCard.pill}`)}
                stepText={t('caseHome.stepOf', { defaultValue: 'Step {{n}} of 4', n: statusCard.step })}
                nextText={statusCard.next ? t(`caseHome.${statusCard.next}`) : null}
                stepNames={sections.map((s) => s.title)}
              />
              {/* v242.71.0 CASEHOME-INLINE-AGENT-1 — the agent renders HERE, inline.
                  What was here before: a static bubble showing `chatMsg`, and
                  below it a composer that only LOOKED like an input — tapping it
                  threw a modal over the screen. So the patient read a sentence
                  about their case, tapped what appeared to be the reply box, and
                  landed in a different surface that opened by greeting them
                  again, with its own separately-derived opening line.
                  The card is the conversation now. `opener` hands the screen's
                  phase-aware line to the store so the thread starts on the same
                  sentence the patient was already reading, instead of a second
                  one derived from the same facts. */}
              <div className={`transition-opacity duration-[400ms] ${chatShowBubble ? 'opacity-100' : 'opacity-0'}`}>
                {/* s120 (Dr. Rami 2026-10-25, canvas "Orella App Chat — Round 1"): the card shows the latest messages and
                    ONE "Open chat" button; typing happens in the bottom sheet (same caseId, same stored thread), which
                    opens from the bottom, keeps its place when he scrolls up, and lets the keyboard be put away. This
                    reverses v242.71.0's in-card composer, by his ruling. */}
                <OrellaChatAgent
                  inline
                  isOpen
                  composer="open"
                  placeholder={t('caseHome.askAnything', 'Ask me anything about your case…')}
                  caseId={currentCase?.id}
                  onNavigate={navigate}
                  initialMessage={agentInitialMessage}
                  onInitialMessageConsumed={() => setAgentInitialMessage(null)}
                  caseContext={agentCaseContext}
                  openTone={cardSection || showFindDoctor ? 'outline' : 'navy'}
                  beforeOpen={cardSection || showFindDoctor ? (
                    <>
                      {cardSection ? (
                        <button type="button" data-cta="casehome-status-action" onClick={() => navigate(cardSection.path)}
                          className="mt-3 w-full min-h-[48px] rounded-full bg-[#15354F] text-white text-[15px] font-bold flex items-center justify-center gap-2 breathe-navy active:scale-[0.99] transition-transform">
                          {cardActionLabel}
                          <ChevronRight size={16} />
                        </button>
                      ) : null}
                      {/* PROD-1422: under the card action, above Open chat; gone once a doctor claims the case. */}
                      {showFindDoctor ? (
                        <button type="button" data-cta="casehome-find-doctor" onClick={() => navigate('/case/share', { state: { findDoctor: true } })}
                          className="mt-2.5 w-full min-h-[48px] rounded-full border-[1.5px] border-[#A7D9FC] bg-[#EFF8FF] text-[#15354F] text-[15px] font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform">
                          <Stethoscope size={17} />
                          {t('shareHub.findDoctor', 'Find a doctor')}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                />
              </div>

              {/* chips */}
              {chatShowContent && (
                <div>
                  {/* v199.8: Inline Share CTA — only in Phase 3 (describe done, share next) */}
                  {/* SHARE-BUTTON-PROMINENT (PROD-401; f2b6cb9d, re-applied on main
                      2026-09-30) — Alex: make the share-with-doctor button bigger
                      and easier to recognise. Was py-3 / 14px bold / 16px icons (~44px tall),
                      smaller than the app's other primary CTAs (ShareConversation
                      send: py-4 15px, 20px icon). Now 56px min-height, 16px
                      extrabold, 20px icon. The inline `animation` used to REPLACE
                      the breathe-blue class animation, so the pulse never ran; it
                      is now chained after the fade-in. The reduced-motion rule in
                      index.css is !important and still wins over it.
                      Held by scripts/check-share-button-prominent.mjs. */}
                  {casePhase === 3 && canShare && (
                    <button
                      data-cta="casehome-share-with-doctor"
                      onClick={() => navigate('/case/share-hub')}
                      className="w-full rounded-2xl font-extrabold text-[16px] leading-tight text-white text-center flex items-center justify-center gap-2.5 active:scale-[0.98] transition-transform breathe-blue"
                      style={{
                        backgroundColor: ORELLA_BLUE,
                        minHeight: 56,
                        padding: '14px 18px',
                        boxShadow: '0 6px 18px rgba(70, 168, 249, 0.40)',
                        animation: 'fadeIn 0.35s ease both, ringBurstBlue 2s ease-out 0.35s infinite',
                        marginTop: 12,
                      }}
                    >
                      <Share2 size={20} strokeWidth={2.4} className="flex-shrink-0" />
                      <span>{t('caseHome.shareWithDoctor')}</span>
                      <ChevronRight size={20} strokeWidth={2.4} className="flex-shrink-0" />
                    </button>
                  )}
                  {showSteps && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
                      {[
                        { label: t('caseHome.howDoesThisWork'), q: 'How does the Orella case process work? Walk me through the steps.' },
                        { label: t('caseHome.whatDoINeed'), q: 'What information or documents do I need to start my case?' },
                        { label: t('caseHome.howLongDoesItTake'), q: 'How long does it take to complete a case on Orella?' },
                      ].map((chip, i) => (
                        <button
                          key={chip.label}
                          onClick={() => openAgentWith(chip.q)}
                          className="active:scale-[0.96] transition-colors cursor-pointer"
                          style={{
                            border: '1.5px solid #CFE6F8', background: '#fff', color: '#1A6FC2',
                            borderRadius: 999, padding: '6px 11px', fontWeight: 600,
                            fontSize: 12, lineHeight: 1,
                            animation: `fadeIn 0.25s ease ${i * 0.08}s both`,
                          }}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {/* ============ PHASE 2: TIMELINE LAYOUT — v143.4 redesign ============ */}
      {chatShowContent && (
      <div className="flex-1 px-4" style={{ animation: 'caseHomeReveal 0.4s ease both' }}>
        <style>{`
          @keyframes caseHomeReveal {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes caseHomeStagger {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes stepShimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
        {/* Timeline Container */}
        {spineLayout === 'pager' && (
          <StagePager
            sections={sections}
            colors={PROGRESS_COLORS}
            textColors={BADGE_TEXT_COLORS}
            activeIndex={activeStageIndex(sections)}
            onOpen={(section) => navigate(section.path)}
            buttonless={!!cardSection}
            labels={{
              complete: t('caseHome.statusComplete', 'Complete'),
              pending: t('caseHome.pendingReview', 'Pending review'),
              pendingConsult: t('caseHome.pendingConsult', 'Doctor reviewing case'),
              locked: t('caseHome.locked', 'Locked'),
              view: t('common.view', 'View'),
              open: t('common.open', 'Open'),
              scrollHint: t('caseHome.scrollBackHint', 'scroll back for earlier steps'),
              // v242.87.0: StageCard reads ten label keys; this object passed
              // six, so `active`, `needsReview`, `uploadNotes` and `viewable`
              // fell through to hardcoded English literals. The "In progress"
              // badge in the b34 #3 screenshot was one of them — untranslated
              // in all eleven locales.
              active: t('caseHome.statusInProgress', 'In Progress'),
              // ★ The pager had no observation label at all, so an observation Deliver
              //   stage fell through to `active` and printed "In Progress" beside its own
              //   "Monitoring" subtitle.
              observation: t('caseHome.observation', 'Observation'),
              // v242.92.0 — this read t('caseHome.pendingReview') and en.json
              //   resolves that to "Pending Review", so "your results are ready,
              //   review them" and "we are waiting on the doctor" rendered the same
              //   word. The inline default never showed, because the key resolved.
              needsReview: t('caseHome.needsReview', 'Needs review'),
              uploadNotes: t('caseHome.uploadDoctorNote', 'Upload notes'),
              viewable: t('common.view', 'View'),
              // s99 (Dr. Rami 2026-10-10): the list draws ONE fixed label per step and names the
              //   current step's action on its button.
              subtitles: {
                describe: t('describe.subtitle'),
                detect: t('detect.subtitle'),
                diagnose: t('caseHome.stepDoctorAssessment', 'Doctor assessment'),
                deliver: t('deliver.subtitle'),
              },
              actions: stageActions,
            }}
          />
        )}

        {spineLayout === 'timeline' && (
        <div className="relative" id="timeline-container">
          {/* Timeline vertical line — full grey background */}
          <div 
            className="absolute w-[3px] rounded-full"
            style={{
              left: '17px',
              top: '24px',
              bottom: '24px',
              backgroundColor: '#E2E8F0',
              zIndex: 0,
            }}
          />
          
          {/* Timeline Steps */}
          <div className="space-y-2.5" style={{ overflow: 'visible' }}>
            {sections.map((section, sectionIndex) => {
              const isLocked = section.locked && !section.complete
              const isActive = section.available && !section.complete
              const isViewable = section.viewable && isLocked
              const canClick = section.available || section.complete || isViewable || section.pending || section.needsReview || section.externalUpload
              const isDiagnoseSection = section.id === 'diagnose'
              const isDeliverSection = section.id === 'deliver'
              // v234.15.0: The MD-agnostic locked Deliver now renders identically to the
              // canonical pastel-green locked Deliver (Deliver = green). The ice-blue "gated"
              // treatment was off-canonical and oversized. The subscription disclaimer + invite
              // CTA moved to a grouped block beneath the box (deliverGatedSubscription). Keeping
              // this flag pinned false lets every gated branch fall through to the standard
              // isDeliverSection-locked styling without touching those getters.
              const isDeliverGatedNoMD = false
              
              const stepColor = PROGRESS_COLORS[section.id] || ORELLA_BLUE
              const badgeTextColor = BADGE_TEXT_COLORS[section.id] || NAVY
              
              // v143.4: Dot shadow uses phase color for depth
              const getDotShadow = () => {
                if (section.complete) return `0 3px 10px ${stepColor}60, 0 0 0 3px ${stepColor}20`
                if (section.needsReview) return `0 3px 10px #F59E0B50, 0 0 0 3px #F59E0B18`
                if (isActive) return `0 3px 12px ${stepColor}50, 0 0 0 4px ${stepColor}18`
                if (section.externalUpload || section.pending) return `0 3px 10px #F59E0B50, 0 0 0 3px #F59E0B18`
                if (isViewable) return `0 3px 10px ${stepColor}40, 0 0 0 3px ${stepColor}15`
                return '0 2px 6px rgba(0,0,0,0.08)'
              }
              
              const getDotColor = () => {
                if (section.complete) return stepColor
                if (section.needsReview) return '#F59E0B'
                if (isActive) return stepColor
                if (section.externalUpload) return '#FCD34D'
                if (section.pending) return '#FCD34D'
                if (isViewable) return stepColor
                if (isDeliverGatedNoMD) return '#CBD5E1'
                if (isDeliverSection && isLocked) return '#D5EFD3'
                return '#E2E8F0'
              }
              
              // v143.4: Outlined pill badge style
              const getStatusPill = () => {
                if (section.complete) {
                  if (isDiagnoseSection && diagnoseAcknowledged) {
                    return { label: t('caseHome.acknowledged'), bg: 'white', color: NAVY, borderColor: NAVY, icon: Lock }
                  }
                  // v143.4: Describe complete → "View Summary"
                  if (section.id === 'describe') {
                    // ★ s111 (TF b78, Dr. Rami 2026-10-19): the list was re-ranked from the patient's
                    //   records since they last opened it — "Updated" until they do (utils/summaryFreshness).
                    if (section.summaryUpdated) {
                      return { label: t('caseHome.summaryUpdated'), bg: '#F2FAF3', color: '#1F4D25', borderColor: '#BFE6C3', icon: RefreshCw }
                    }
                    return { label: t('caseHome.viewSummary'), bg: 'white', color: badgeTextColor, borderColor: stepColor, icon: Eye }
                  }
                  if (isDeliverSection && section.deliverState === 'all-done') {
                    return { label: t('caseHome.allDone'), bg: 'white', color: '#1B873A', borderColor: '#66D170', icon: CheckCircle }
                  }
                  if (isDeliverSection && section.deliverState === 'complete') {
                    return { label: t('caseHome.careComplete'), bg: 'white', color: '#059669', borderColor: '#34D399', icon: CheckCircle }
                  }
                  return { label: t('caseHome.complete'), bg: 'white', color: badgeTextColor, borderColor: stepColor, icon: CheckCircle }
                }
                if (section.needsReview) {
                  return { label: t('caseHome.reviewResults'), bg: 'white', color: '#92400E', borderColor: '#D97706', icon: AlertCircle }
                }
                // ★★★ OBSERVATION IS DERIVED HERE, NOT WAITED FOR. The badge read
                //   "In Progress" on a case whose own subtitle said "Monitoring", because
                //   the observation branch below keys on `deliverState` — and THAT VALUE IS
                //   WRITTEN BY A useEffect ON THE DELIVER SCREEN (DeliverScreen:1573).
                //   So the badge only became correct once the patient had opened Deliver,
                //   on the screen whose whole job is to send them to Deliver. A fact this
                //   component already knows must not wait on a visit to another one:
                //   `section.observation` is computed right here (line ~933) from the
                //   encounter category and the case status, and the subtitle has been using
                //   it correctly all along.
                // ★★ AND IT WORE AMBER. Not a brand token, and on the patient's screens it
                //   still means asap/required — the opposite of what an observation asks.
                //   Navy on ice, matching the observation card the badge leads to.
                if (isDeliverSection && isActive && section.observation) {
                  return { label: t('caseHome.observation'), bg: 'white', color: NAVY, borderColor: '#A7D9FC', icon: Eye }
                }
                if (isDeliverSection && isActive && section.deliverState) {
                  const ds = section.deliverState
                  if (ds === 'submitted') return { label: t('caseHome.submitted'), bg: 'white', color: '#059669', borderColor: '#34D399', icon: CheckCircle }
                  if (ds === 'scheduled') return { label: t('caseHome.scheduled'), bg: 'white', color: '#0284C7', borderColor: '#38BDF8', icon: Calendar }
                  if (ds === 'observation') return { label: t('caseHome.observation'), bg: 'white', color: NAVY, borderColor: '#A7D9FC', icon: Eye }
                  if (ds === 'referral') return { label: t('caseHome.referred'), bg: 'white', color: '#7C3AED', borderColor: '#A78BFA', icon: Send }
                }
                if (isActive) {
                  return { label: section.hasContent ? 'Add More' : 'In Progress', bg: 'white', color: badgeTextColor, borderColor: stepColor, icon: section.hasContent ? FilePlus : Clock }
                }
                if (section.externalUpload) {
                  return { label: t('caseHome.inProgress'), bg: 'white', color: '#92400E', borderColor: '#D97706', icon: Clock }
                }
                if (section.pending) {
                  return { label: t('caseHome.pendingReview'), bg: 'white', color: '#92400E', borderColor: '#D97706', icon: Clock }
                }
                if (isViewable) {
                  return { label: t('caseHome.view'), bg: 'white', color: badgeTextColor, borderColor: stepColor, icon: Eye }
                }
                if (isDeliverGatedNoMD) {
                  return { label: t('caseHome.locked'), bg: '#F6FBFF', color: '#3A7BBF', borderColor: '#A7D9FC', icon: Lock }
                }
                return { label: t('caseHome.locked'), bg: '#FAFAFA', color: '#94A3B8', borderColor: '#D1D5DB', icon: Lock }
              }
              
              const statusPill = getStatusPill()
              const StatusIcon = statusPill.icon
              
              // v143.4: Card border color
              const getCardBorder = () => {
                if (section.complete) return `${stepColor}70`
                if (section.needsReview) return '#F59E0B50'
                if (isActive) return stepColor
                if (section.externalUpload || section.pending) return '#FCD34D80'
                if (isViewable) return `${stepColor}60`
                if (isDeliverGatedNoMD) return '#A7D9FC'
                if (isDeliverSection && isLocked) return '#D5EFD3'
                return '#E5E7EB'
              }
              
              // v143.4: Card background
              const getCardBg = () => {
                if (section.complete) return 'white'
                if (section.needsReview) return '#FFFBEB'
                if (isActive) return `${stepColor}05`
                if (section.externalUpload || section.pending) return '#FFFBEB'
                if (isViewable) return 'white'
                if (isDeliverGatedNoMD) return '#FBFEFF'
                if (isLocked) return '#FAFAFA'
                return 'white'
              }
              
              // v143.4: Only locked items get dimmed — completed stay vivid
              const cardOpacity = (isLocked && !isViewable && !section.pending && !section.needsReview && !section.externalUpload) ? 0.55 : 1
              
              return (
                <div key={section.id} className="flex items-stretch gap-3.5 relative" style={{ animation: `caseHomeStagger 0.3s ease both`, animationDelay: `${sectionIndex * 80}ms` }}>
                  {/* v143.4: Gradient tail from this phase color to next phase color */}
                  {sectionIndex < sections.length - 1 && (() => {
                    const nextSection = sections[sectionIndex + 1]
                    const nextIsOpen = nextSection.complete || nextSection.available || nextSection.needsReview || nextSection.pending || nextSection.externalUpload || (nextSection.viewable && !nextSection.locked)
                    const nextColor = PROGRESS_COLORS[nextSection.id] || ORELLA_BLUE
                    return nextIsOpen ? (
                      <div 
                        className="absolute w-[3px] rounded-full"
                        style={{
                          left: '16.5px',
                          top: '38px',
                          bottom: '-12px',
                          background: `linear-gradient(to bottom, ${stepColor}, ${nextColor})`,
                          transition: 'opacity 0.3s ease',
                          zIndex: 1,
                        }}
                      />
                    ) : null
                  })()}
                  
                  {/* v143.4: Larger timeline dot (36px) with phase-colored shadow */}
                  <div className="flex flex-col items-center pt-3 z-10">
                    <div 
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: getDotColor(),
                        boxShadow: getDotShadow(),
                      }}
                    >
                      {section.complete ? (
                        <Check size={17} className="text-white" strokeWidth={3} />
                      ) : section.needsReview ? (
                        <AlertCircle size={15} className="text-white" />
                      ) : section.externalUpload ? (
                        <Clock size={15} className="text-white" />
                      ) : isActive ? (
                        <div className="w-3 h-3 bg-white rounded-full" style={{ boxShadow: '0 0 8px rgba(255,255,255,0.6)' }} />
                      ) : section.pending ? (
                        <Clock size={15} className="text-white" />
                      ) : isViewable ? (
                        <Eye size={15} className="text-white" />
                      ) : isDeliverGatedNoMD ? (
                        <Lock size={14} style={{ color: '#64748B' }} />
                      ) : isDeliverSection && isLocked ? (
                        <Lock size={14} style={{ color: '#66D170' }} />
                      ) : (
                        <Lock size={14} style={{ color: '#94A3B8' }} />
                      )}
                    </div>
                  </div>
                  
                  {/* v143.4: Step Card — bigger labels, outlined badges */}
                  <button
                    onClick={() => canClick && navigate(section.path)}
                    disabled={!canClick}
                    className={`flex-1 rounded-xl border-2 overflow-hidden text-left transition-all relative ${
                      isActive || section.needsReview ? 'breathe-ring' : ''
                    }`}
                    style={{
                      borderColor: getCardBorder(),
                      backgroundColor: getCardBg(),
                      opacity: cardOpacity,
                    }}
                  >
                    {/* v177.5.0: Pulse on active/pending action cards */}
                    <div className="flex items-center justify-between px-4 py-3.5 relative">
                      <div className="flex items-center gap-3">
                        {/* Step indicator dot */}
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: (isLocked && !isViewable && !section.pending && !section.needsReview && !section.externalUpload)
                              ? (isDeliverGatedNoMD ? '#CBD5E1' : isDeliverSection ? '#D5EFD3' : '#CBD5E1')
                              : (section.pending || section.needsReview || section.externalUpload ? '#FCD34D' : stepColor),
                            boxShadow: (isLocked && !isViewable) ? 'none' : `0 1px 4px ${stepColor}40`,
                          }}
                        />
                        <div>
                          <h3 
                            className="font-bold"
                            style={{
                              fontSize: 18,
                              letterSpacing: '-0.015em',
                              lineHeight: 1.2,
                              color: (isLocked && !isViewable && !section.complete && !section.pending && !section.needsReview && !section.externalUpload)
                                ? '#94A3B8'
                                : NAVY,
                            }}
                          >
                            {section.title}
                          </h3>
                          <p style={{
                            fontSize: 13,
                            fontWeight: 500,
                            marginTop: 3,
                            color: (isLocked && !isViewable && !section.complete && !section.pending && !section.needsReview && !section.externalUpload)
                              ? '#B0B8C4'
                              : '#64748B',
                          }}>
                            {section.subtitle}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* v143.4: Outlined pill badge */}
                        <span 
                          className="flex items-center gap-1.5 font-bold whitespace-nowrap"
                          style={{ 
                            fontSize: 12.5,
                            backgroundColor: statusPill.bg, 
                            color: statusPill.color,
                            border: `1.5px solid ${statusPill.borderColor}`,
                            padding: '6px 14px',
                            borderRadius: 20,
                          }}
                        >
                          <StatusIcon size={13} />
                          {statusPill.label}
                        </span>
                        
                        {canClick && (
                          <ChevronRight 
                            size={20} 
                            style={{ color: '#94A3B8' }} 
                          />
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
        )}

        {/* v234.15.0: External-note case, note processed, no Orella MD. The Deliver box above
            is the canonical pastel-green locked card; the subscription disclaimer + invite CTA
            are grouped here, flush as one unit and indented (50px = dot col 36px + gap 14px) to
            align under the Deliver card. Surfaces only after the note is processed
            (deliverGatedSubscription), not the moment the case is shared. */}
        {deliverGatedSubscription && (
          <div className="mt-2" style={{ marginLeft: '50px' }}>
            <div
              className="flex items-start gap-2.5"
              style={{ background: '#E9F8FF', border: '1px solid #A7D9FC', borderRadius: '12px 12px 0 0', padding: '11px 14px' }}
            >
              <Lock size={15} style={{ color: '#3A7BBF', flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45, color: '#15354F' }}>
                {t('caseHome.subtitleDeliverNeedsOrella', 'Available for doctors subscribed to Orella — remind yours to join to unlock')}
              </p>
            </div>
            <button
              onClick={() => setShowRemindSheet(true)}
              className="flex w-full items-center justify-center gap-2 text-[13px] font-bold transition-all"
              style={{ border: '1px solid #A7D9FC', borderTop: 0, background: '#FFFFFF', color: '#15354F', borderRadius: '0 0 12px 12px', padding: '12px' }}
            >
              <Mail size={16} />
              {t('remindDoctor.cta', 'Remind your doctor')}
            </button>
          </div>
        )}

        {/* ============ v242.73.0 CASEHOME-GROUPS-1 — "Your visit" ============
            Was: up to five stacked white cards — completed visits, the initial
            appointment, a pending reopen, the follow-up, a generic fallback —
            each with its own 3px status stripe, all permanently expanded, with
            the assigned doctor rendered as a SIXTH card further down the page.
            One group now. The header carries the appointment itself, so a shut
            group still answers the only question the patient came with. */}
        {(completedAppointments.length > 0 || upcomingAppointment || formattedOriginalAppointment || formattedFollowUpAppointment || hasPendingReopen || followUpWasDeclined || casePhase >= 4) && (() => {
          // ★ `|| casePhase >= 4` is load-bearing. The banner this group replaced
          // rendered for casePhase 4-5 FLAT, with no appointment condition on it,
          // because it carried three things: the doctor, the case status, and —
          // when there is no doctor yet — the SHARE CODE. Gating the group on an
          // appointment silently deleted all three for every case shared by code
          // and still waiting for a doctor, which is the single most common state
          // between sharing and the first confirmation.
          const shareCodeDisplay = currentCase.shareCode || currentCase.share_code || ''
          const hasAnyAppointment = !!(completedAppointments.length > 0 || upcomingAppointment || formattedOriginalAppointment || formattedFollowUpAppointment || hasPendingReopen || followUpWasDeclined || followUpPhysicianCancel)
          // ★★★ TEST 1.19 (2026-09-21) — THE TERNARY THAT USED TO LIVE HERE WAS
          //   `code ? awaitingDoctor : notSharedYet`, and it decided "is a doctor
          //   being awaited" on the MERE EXISTENCE of a share code. A minted code
          //   is not an address to anybody: no physician has been asked, so
          //   nobody is awaited. One resolver now answers this for the header
          //   meta, the sub-line and the tag, so the three cannot disagree —
          //   see resolveDoctorWaitState in utils/caseShareState.js for which
          //   state is which and why state A is an instruction, not a status.
          const waitLabels = doctorWaitLabels(currentCase)
          const visitMetaFallback = () => {
            if (waitLabels.state === 'with_doctor' || !waitLabels.tagKey) return undefined
            return t(waitLabels.tagKey)
          }
          // s99 D475: faxed, not yet accepted -> the office, "Sent to".
          //   PROD-1422: never while a doctor is on the case (the read now also runs for a named own doctor).
          const sentOffice = officeSend && !hasAttendingDoctor(currentCase) ? officeLines(officeSend) : null
          // s126 (note 11): the next booked visit across every encounter, else the most recent one; the initial
          //   request's label (agentTemporal, which Ask Orella also reads) only when the case has no appointment row.
          //   Only the current physician's rows (PROD-1020); the initial row never names a time its request no longer has
          //   (declined, PROD-983; cancelled by the physician, PROD-1137).
          const initialGone = requestDeclined(currentCase.appointmentRequest) || requestCancelledByPhysician(currentCase.appointmentRequest)
          const headerVisit = pickHeaderVisit(rowsOfCurrentPhysician(currentCase, allAppointments), new Date(),
            (a) => isOutsideAppointment(a) || (initialGone && !!originalAppointment?.id && a.id === originalAppointment.id))
          const headerParts = headerVisit ? visitParts(headerVisit.row, formatDate, formatAppointmentTime) : null
          const headerLabel = headerParts?.date ? (headerParts.time ? `${headerParts.date} at ${headerParts.time}` : headerParts.date) : ''
          const visitMeta = headerLabel || agentTemporal.appointmentLabel
            || (hasAnyAppointment ? undefined : (sentOffice ? t('caseHome.sentMeta', 'Sent') : visitMetaFallback()))
          // The doctor, folded in from the old post-share banner card.
          const isOrellaPath = currentCase.doctorSource !== 'external' && currentCase.doctor_source !== 'external'
          const orellaDoc = currentCase.doctor
          const extDoc = currentCase.externalDoctor || currentCase.external_doctor
          const hasDoctor = isOrellaPath ? !!orellaDoc : !!extDoc
          const drFirstName = isOrellaPath ? (orellaDoc?.first_name || orellaDoc?.firstName || '') : (extDoc?.name?.split(' ')[1] || extDoc?.firstName || '')
          const drLastName = isOrellaPath ? (orellaDoc?.last_name || orellaDoc?.lastName || '') : (extDoc?.name?.split(' ').slice(-1)[0] || extDoc?.lastName || '')
          const drDisplayName = isOrellaPath
            ? (orellaDoc ? clinicianName(withCaseClinician({ ...orellaDoc, first_name: drFirstName, last_name: drLastName }, currentCase)) : currentCase.sentToDoctorName || t('caseHome.yourOrellaDoctor'))
            : (extDoc?.name || currentCase.sentToDoctorName || t('caseHome.yourDoctor'))
          const drSpecialty = isOrellaPath ? (orellaDoc?.specialty || '') : (extDoc?.specialty || '')
          // PROD-983: the approved sentence (en + es) the appointment_rejected notification uses.
          //   Its key carries "Dr." itself, so the name goes in without it.
          const declinedDoctorName = (`${drFirstName} ${drLastName}`.trim()
            || String(currentCase.sentToDoctorName || '').trim()).replace(/^Dr\.?\s+/i, '')
          // s119 D530: the approved {{clinician}} / {{clinicianSubject}} forms for the two sentences below
          const declinedForms = clinicianForms(isOrellaPath && orellaDoc ? withCaseClinician(orellaDoc, currentCase) : clinicianFromText(declinedDoctorName))
          const declinedLine = t('notifications.appointmentRejected.line', { ...declinedForms, doctorName: declinedDoctorName })
          // PROD-1137: "Dr. X cancelled this appointment." and, with a known code, "Reason: <sentence>".
          //   An MA's cancel reads the same (Alex 2026-10-18). Never the physician's private note.
          const physicianCancelLine = (pc) => {
            const reasonKey = cancelReasonKey(pc?.reasonCode)
            return [t('caseHome.cancelledByPhysician', { ...declinedForms, doctorName: declinedDoctorName }),
              reasonKey ? t('caseHome.cancelReason', { reason: t(reasonKey) }) : null].filter(Boolean).join(' ')
          }
          // ★★ "Awaiting assignment" was the worst line on this card: it names a
          //   QUEUE and an ASSIGNER, and on the share-code path neither exists.
          //   Nobody assigns a code-shared case — the patient hands the code over
          //   and any doctor holding it opens the case himself.
          const drSub = hasDoctor
            ? [drSpecialty, isOrellaPath ? t('caseHome.orellaDoctor') : null].filter(Boolean).join(' · ')
            : (waitLabels.subKey ? t(waitLabels.subKey) : undefined)
          const drDocId = isOrellaPath ? (orellaDoc?.id || currentCase.sentToDoctor || currentCase.doctor_id) : null
          // v242.101.0 (TF b38) — the MD's photo. Same accessor DiagnoseScreen's
          // doctor card uses, so the two surfaces cannot disagree about which
          // field holds it. External doctors have no Orella profile photo.
          const drPhoto = isOrellaPath
            ? (orellaDoc?.profile_photo || orellaDoc?.profilePhoto
               || currentCase.sentToDoctorData?.profile_photo || null)
            : null
          const drTag = (() => {
            switch (currentCase.status) {
              case 'completed':
              case 'in_follow_up': return <Tag tone="ok">{t('caseHome.complete')}</Tag>
              case 'checked_in': return <Tag tone="ok">{t('caseHome.statusCheckedIn', 'Checked In')}</Tag>
              // PROD-983: the decline of a held reschedule leaves the case column 'scheduled'; with
              //   the request declined nothing is scheduled, so it reads as with the doctor.
              case 'scheduled': return requestDeclined(currentCase.appointmentRequest) || requestCancelledByPhysician(currentCase.appointmentRequest) // PROD-1137
                ? <Tag tone="info">{t('caseHome.reviewing')}</Tag>
                : <Tag tone="info">{t('caseHome.scheduled') || 'Scheduled'}</Tag>
              case 'in_progress':
              case 'claimed': return <Tag tone="info">{t('caseHome.reviewing')}</Tag>
              // ★★★ v242.87.0 (TF b35 #26) — "I haven't selected a doc but it
              //   says awaiting doctor."
              //   This used to be `default: <Tag tone="warn">Awaiting Doctor`.
              //   A catch-all over `status` swallowed draft, ready_to_share,
              //   shared, submitted, finalized and in_observation into one amber
              //   pill, so a patient who had chosen nobody and shared nothing was
              //   told a doctor was being awaited. `ready_to_share` reaches this
              //   arm through the code-share phase floor at :643, which is
              //   precisely the state he screenshotted.
              //   "Awaiting" is a claim that someone has been asked. It needs
              //   the two facts already computed above, not the status alone:
              //   is there a doctor, and has anything been shared.
              //   ★★★ TEST 1.19 (2026-09-21) — `if (drShareCode) return warn
              //   Awaiting Doctor` was still the same false claim one level down:
              //   a code is not an address. An `external` case was told to wait
              //   for an Orella physician who is never coming, and a patient who
              //   had minted a code and handed it to nobody was told the same.
              //   The tag now comes from the one resolver, which reaches a
              //   waiting word ONLY when a physician was genuinely addressed.
              default:
                if (hasDoctor) return <Tag tone="info">{t('caseHome.assigned', 'Assigned')}</Tag>
                if (waitLabels.tagKey) return <Tag tone={waitLabels.tagTone}>{t(waitLabels.tagKey)}</Tag>
                return <Tag>{t('caseHome.notSharedYet', 'Not shared yet')}</Tag>
            }
          })()
          return (
          <CaseGroup icon={Calendar} name={t('caseHome.groupVisit', 'Your visit')} meta={visitMeta} defaultOpen>
            {/* s106 (canvas "Orella Referrals Screens" A5/A6/A8): the referral this visit waits for, if any. */}
            {currentCase.id && !String(currentCase.id).startsWith('local-') && (
              <ReferralRequestCard caseId={currentCase.id} t={t}
                doctorName={currentCase.sentToDoctorName || (currentCase.doctor ? clinicianName(withCaseClinician(currentCase.doctor, currentCase)) : '')} />
            )}
            {/* Completed visits */}
            {completedVisitRows.map((apt, idx) => (
              <GroupLine
                key={apt.id || idx}
                first={idx === 0}
                icon={Check}
                iconBg="#F1F5F9"
                iconColor="#94A3B8"
                title={t('caseHome.completedVisit')}
                sub={`${(() => { const v = visitParts(apt, formatDate, formatAppointmentTime); return v.date ? (v.time ? `${v.date} · ${v.time}` : v.date) : `${apt.date || ''}` })()} • ${apt.visitType === 'telehealth' ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}`}
                onClick={() => navigate('/visits', { state: { appointmentId: apt.id } })}
              />
            ))}

            {/* Initial appointment */}
            {formattedOriginalAppointment && formattedOriginalAppointment.date && (() => {
              const isConfirmedOrModified = ['confirmed', 'rescheduled', 'modified', 'completed', 'finalized'].includes(formattedOriginalAppointment.status)
              // ★ PROD-983: the physician declined this request (or the held reschedule of it). It
              //   read Pending before (the status fell to the non-confirmed branch). Declined now, in
              //   the approved notification sentence; no visit link (the row is gone).
              if (isDeclined(formattedOriginalAppointment.status) && !shouldGreyInitialAppointment) {
                return (
                  <GroupLine
                    first={completedVisitRows.length === 0}
                    icon={X}
                    iconBg="rgba(239,68,68,0.08)"
                    iconColor="#EF4444"
                    title={`${formattedOriginalAppointment.date} · ${formattedOriginalAppointment.time}`}
                    sub={declinedLine}
                  />
                )
              }
              // ★ PROD-1137: the physician cancelled this confirmed visit. It read Pending before, with
              //   Check in offered (the 'cancelled' status fell to the non-confirmed branch). Now: the
              //   cancelled time, "Dr. X cancelled this appointment." and the reason sentence. The
              //   actions below offer "Choose a new time" (same physician) and Cancel & withdraw.
              if (initialPhysicianCancel && !shouldGreyInitialAppointment) {
                return (
                  <GroupLine
                    first={completedVisitRows.length === 0}
                    icon={X}
                    iconBg="rgba(239,68,68,0.08)"
                    iconColor="#EF4444"
                    title={`${formattedOriginalAppointment.date} · ${formattedOriginalAppointment.time}`}
                    sub={physicianCancelLine(initialPhysicianCancel)}
                  />
                )
              }
              // ★ P20: a pending reschedule with a valid held entry keeps the booked visit on this line.
              if (heldChange && !shouldGreyInitialAppointment) {
                return (
                  <>
                    <GroupLine
                      first={completedVisitRows.length === 0}
                      icon={CheckCircle}
                      iconBg="#E7F8EC"
                      iconColor="#1B873A"
                      title={`${heldChange.booked.date} · ${heldChange.booked.time}`}
                      sub={heldChange.booked.visitType === 'telehealth' ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}
                      onClick={() => navigate('/visits', { state: { appointmentId: originalAppointment?.id } })}
                      tag={<Tag tone="ok">{t('caseHome.appointmentConfirmed', 'Confirmed')}</Tag>}
                    />
                    <GroupLine
                      icon={Clock}
                      iconBg="#FEF3C7"
                      iconColor="#92400E"
                      title={t('caseHome.changeRequested', { date: heldChange.requested.date, time: heldChange.requested.time })}
                    />
                  </>
                )
              }
              return (
                <GroupLine
                  first={completedVisitRows.length === 0}
                  icon={shouldGreyInitialAppointment || isConfirmedOrModified ? CheckCircle : formattedOriginalAppointment.status === CANCELLED ? X : Clock}
                  iconBg={shouldGreyInitialAppointment || formattedOriginalAppointment.status === CANCELLED ? '#F1F5F9' : isConfirmedOrModified ? '#E7F8EC' : '#FEF3C7'}
                  iconColor={shouldGreyInitialAppointment || formattedOriginalAppointment.status === CANCELLED ? '#94A3B8' : isConfirmedOrModified ? '#1B873A' : '#92400E'}
                  title={`${formattedOriginalAppointment.date} · ${formattedOriginalAppointment.time}`}
                  sub={formattedOriginalAppointment.visitType === 'telehealth' ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}
                  onClick={() => navigate('/visits', { state: { appointmentId: originalAppointment?.id } })}
                  tag={shouldGreyInitialAppointment
                    ? <Tag>{t('caseHome.initialVisit')}</Tag>
                    : isConfirmedOrModified
                      ? <Tag tone="ok">{formattedOriginalAppointment.status === 'confirmed' ? t('caseHome.appointmentConfirmed', 'Confirmed') : t('caseHome.updated', 'Updated')}</Tag>
                      : formattedOriginalAppointment.status === CANCELLED // PROD-1137: a cancelled visit is never Pending
                        ? undefined
                        : <Tag tone="warn">{t('caseHome.pending', 'Pending')}</Tag>}
                />
              )
            })()}

            {/* Pending follow-up request (reopen / observation bridge) */}
            {hasPendingReopen && (() => {
              const isObsBridge = currentCase.appointmentRequest?.observationFinding === true
              const dateSource = isObsBridge
                ? (currentCase.appointmentRequest?.requestedDate || currentEncounterAppointment?.date)
                : (currentEncounterAppointment?.date || currentCase.appointmentRequest?.requestedDate)
              const timeSource = isObsBridge
                ? (currentCase.appointmentRequest?.requestedTime || currentEncounterAppointment?.time)
                : (currentEncounterAppointment?.time || currentCase.appointmentRequest?.requestedTime)
              const isVideo = isObsBridge
                ? (currentCase.appointmentRequest?.appointmentType === 'telehealth')
                : (currentEncounterAppointment?.type === 'video' || currentCase.appointmentRequest?.appointmentType === 'telehealth')
              return (
                <GroupLine
                  icon={Clock}
                  iconBg="#FEF3C7"
                  iconColor="#92400E"
                  title={t('caseHome.followupRequested')}
                  sub={`${dateSource ? formatDate(dateSource, { weekday: 'short', month: 'short', day: 'numeric' }) : t('caseHome.dateTBD')}${timeSource ? ` at ${formatAppointmentTime(timeSource)}` : ''} • ${isVideo ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}`}
                  tag={<Tag tone="warn">{t('caseHome.pending', 'Pending')}</Tag>}
                />
              )
            })()}

            {/* PROD-983: the physician declined the held follow-up reschedule. The API cleared its
                date and time, so the line names no time; tapping it opens the existing follow-up
                scheduling screen to ask for another time. */}
            {followUpWasDeclined && !(formattedFollowUpAppointment && formattedFollowUpAppointment.date) && (
              <GroupLine
                icon={X}
                iconBg="rgba(239,68,68,0.08)"
                iconColor="#EF4444"
                title={declinedLine}
                onClick={() => navigate('/case/schedule-followup', { state: { returnTo: '/case/home' } })}
              />
            )}

            {/* PROD-1137: the physician cancelled the confirmed follow-up visit. Tapping the line (or
                "Choose a new time" below) opens the existing follow-up scheduling screen. */}
            {followUpPhysicianCancel && !(formattedFollowUpAppointment && formattedFollowUpAppointment.date) && (
              <GroupLine
                icon={X}
                iconBg="rgba(239,68,68,0.08)"
                iconColor="#EF4444"
                title={followUpPhysicianCancel.date
                  ? `${formatDate(followUpPhysicianCancel.date, { weekday: 'short', month: 'short', day: 'numeric' })}${followUpPhysicianCancel.time ? ` · ${formatAppointmentTime(followUpPhysicianCancel.time)}` : ''}`
                  : physicianCancelLine(followUpPhysicianCancel)}
                sub={followUpPhysicianCancel.date ? physicianCancelLine(followUpPhysicianCancel) : undefined}
                onClick={() => navigate('/case/schedule-followup', { state: { returnTo: '/case/home' } })}
              />
            )}

            {/* Confirmed follow-up */}
            {formattedFollowUpAppointment && formattedFollowUpAppointment.date && (
              <GroupLine
                icon={followUpAwaitsReschedule ? Clock : Calendar}
                iconBg={followUpAwaitsReschedule ? '#FEF3C7' : '#E7F8EC'}
                iconColor={followUpAwaitsReschedule ? '#92400E' : '#1B873A'}
                title={`${formattedFollowUpAppointment.date} · ${formattedFollowUpAppointment.time}`}
                sub={`${t('caseHome.followupScheduled')} • ${formattedFollowUpAppointment.visitType === 'telehealth' ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}`}
                onClick={() => navigate('/visits', { state: { appointmentId: currentEncounterAppointment?.id || followUpData?.appointmentId } })}
                tag={followUpAwaitsReschedule /* PROD-921: display only */
                  ? <Tag tone="warn">{t('caseHome.pending', 'Pending')}</Tag>
                  : <Tag tone="ok">{t('caseHome.appointmentConfirmed', 'Confirmed')}</Tag>}
              />
            )}

            {/* Generic upcoming (fallback) */}
            {upcomingAppointment && !formattedFollowUpAppointment && !formattedOriginalAppointment && (
              <GroupLine
                first={completedVisitRows.length === 0}
                icon={Calendar}
                iconBg="#E7F8EC"
                iconColor="#1B873A"
                title={`${upcomingAppointment.date} · ${upcomingAppointment.time}`}
                sub={upcomingAppointment.visitType === 'telehealth' ? t('caseHome.virtualVisit') : t('caseHome.inPerson')}
                onClick={() => navigate('/visits', { state: { appointmentId: upcomingAppointment?.id } })}
                tag={<Tag tone="ok">{t('caseHome.appointmentConfirmed', 'Confirmed')}</Tag>}
              />
            )}

            {/* The doctor — one fact with the appointment, two cards before. */}
            {(casePhase >= 4 || hasDoctor) && (
              <GroupLine
                first={!hasAnyAppointment}
                icon={UserCheck}
                photo={drPhoto}
                /* s105 (TestFlight b71): the code line stands out, and only where it is tappable (below). */
                highlight={!hasDoctor && !sentOffice && !!shareCodeDisplay && !drDocId && isExternalPath}
                title={hasDoctor || !shareCodeDisplay ? drDisplayName
                  : sentOffice ? t('caseHome.sentTo', { defaultValue: 'Sent to {{name}}', name: sentOffice.name || sentOffice.practice })
                  : t('diagnose.codeDisplay', { code: shareCodeDisplay })}
                sub={!hasDoctor && sentOffice
                  ? (officeSend.status === 'delivered'
                      ? t('caseHome.waitingAccept', 'Waiting for the doctor to accept your case')
                      : t('caseHome.sendingReport', 'Sending your Case Report…'))
                  : drSub}
                /* v242.78.0 (TF-30) — "Should be clickable to view the share
                   code screen that was generated." When there is no doctor yet
                   this row IS the share code, and it was inert: the one thing
                   on Case Home that names the code could not open it. Doctor ->
                   their profile; code -> the Visit Prep screen that generated
                   it, which is where the code, the QR, the wallet pass and the
                   PDF already live. */
                onClick={drDocId
                  ? () => navigate(`/doctor/${drDocId}`, { state: { mode: 'view' } })
                  : (shareCodeDisplay && isExternalPath)
                    /* Gated on isExternalPath because DiagnoseScreen's
                       showShareCode branch lives INSIDE `if (isExternalPath)`
                       (its line 1541). A code-shared case on the Orella path
                       would navigate to a screen that silently ignores the
                       state — a tap that looks broken is worse than a row that
                       is plainly inert. Checked the branch, did not assume it. */
                    ? () => navigate('/case/share-hub') // s98 D468: the code, the PDF and the sends live on the hub
                    : undefined}
                tag={!hasDoctor && sentOffice
                  ? <Tag tone="info">{officeSend.status === 'delivered' ? t('caseHome.officeDeliveredTag', 'Awaiting') : t('caseHome.sendingTag', 'Sending')}</Tag>
                  : drTag}
              />
            )}

            {/* Actions for the initial visit. The follow-up card's own
                View/Change moved here too — one appointment, one control row. */}
            {formattedOriginalAppointment && formattedOriginalAppointment.date && !shouldGreyInitialAppointment && (
              <AppointmentActions
                eligible={agentTemporal.checkInEligible}
                // PROD-983: a declined request offers no check-in; Change asks for another time.
                // PROD-1137: nor does a visit the physician cancelled (any cancelled visit); Change reads
                //   "Choose a new time" there and books with the same physician.
                showCheckIn={!isDeclined(formattedOriginalAppointment.status) && formattedOriginalAppointment.status !== CANCELLED}
                checkedIn={currentCase.status === 'checked_in'}
                onView={() => navigate('/visits', { state: { appointmentId: originalAppointment?.id } })}
                onCheckIn={() => navigate('/case/diagnose', { state: { openCheckIn: true } })}
                onChange={() => openRescheduleModal('initial')}
                onCancel={() => setShowWithdrawModal(true)}
                labels={{
                  cancel: t('diagnose.cancelWithdrawCase'),
                  view: t('caseHome.apptView', 'View'),
                  checkIn: t('caseHome.apptCheckIn', 'Check in'),
                  checkedIn: t('caseHome.apptCheckedIn', 'Checked in'),
                  change: initialPhysicianCancel ? t('caseHome.chooseNewTime') : t('caseHome.apptChange', 'Change'),
                  closedNote: t('caseHome.apptCheckInClosed', 'Check-in opens 24 hours before your visit'),
                }}
              />
            )}
            {!formattedOriginalAppointment && formattedFollowUpAppointment && formattedFollowUpAppointment.date && (
              <AppointmentActions
                showCheckIn={false}
                onView={() => navigate('/visits', { state: { appointmentId: currentEncounterAppointment?.id || followUpData?.appointmentId } })}
                onChange={() => openRescheduleModal('follow_up')}
                labels={{ view: t('caseHome.apptView', 'View'), change: t('caseHome.apptChange', 'Change') }}
              />
            )}
            {/* PROD-1137: "Choose a new time" for a follow-up the physician cancelled, wherever the initial
                visit's control row is not drawn (the initial visit is past, or there is none). */}
            {!(formattedOriginalAppointment && formattedOriginalAppointment.date && !shouldGreyInitialAppointment) && followUpPhysicianCancel && !(formattedFollowUpAppointment && formattedFollowUpAppointment.date) && (
              <AppointmentActions
                showCheckIn={false}
                onView={() => navigate('/visits')}
                onChange={() => navigate('/case/schedule-followup', { state: { returnTo: '/case/home' } })}
                labels={{ view: t('caseHome.apptView', 'View'), change: t('caseHome.chooseNewTime') }}
              />
            )}

            {/* ★★★ v242.152.0 WITHDRAW-AFFORDANCE-1 — the case can be taken back
                even when there is nothing to reschedule. Both blocks above hang
                the withdraw control off an APPOINTMENT; a claimed-but-unscheduled
                case has none, so the patient had no way to press an action the
                API has accepted since v138.1.0. This row is the same action, on
                the permission rather than on the appointment. */}
            {!(formattedOriginalAppointment && formattedOriginalAppointment.date)
              && !(formattedFollowUpAppointment && formattedFollowUpAppointment.date)
              && canWithdrawCase(currentCase) && (
              <button
                onClick={() => setShowWithdrawModal(true)}
                className="w-full py-2.5 rounded-full border-[1.5px] border-red-200 text-red-500 text-[13px] font-semibold flex items-center justify-center gap-1.5 mt-2 active:scale-[0.98] transition-all"
                style={{ backgroundColor: '#fef2f2' }}
              >
                {t('diagnose.cancelWithdrawCase')}
              </button>
            )}
          </CaseGroup>
          )
        })()}

        {/* v242.77.0 (TF-26) — "Remove that second share with doctor box its
            already at the top in the chat."
            The pre-share CTA lived here AND inside the chat card, so phase 3
            rendered the same blue "Share with Doctor" button twice on one
            screen, ~600px apart, both going to /case/share-choice. The chat
            card's copy is the one that survives: it sits with the sentence that
            explains why to press it. The post-share doctor banner that also
            lived in this block moved into "Your visit" in v242.73.0, so this
            section had nothing left to render. */}
        {/* ============ v242.73.0 CASEHOME-GROUPS-1 — records + history ============
            Was: a 2-up RESOURCES grid of tiles, then two loose outline buttons
            for archive and delete under a hairline. Both permanently expanded,
            in two more visual languages, below the appointment cards and the
            doctor card. Now two collapsible groups whose headers state what is
            inside them while shut. */}
        {(() => {
          const isFinalized = currentCase.status === 'completed' || !!currentCase.finalizedAt
          const documents = currentCase.documents || {}
          const followUp = currentCase.followUp || {}
          // v239.0 spread guard — these can arrive as truthy non-arrays and
          // `|| []` only catches falsy, which crashed the whole render.
          const asArr = (v) => (Array.isArray(v) ? v : [])
          const authRefCount =
            [...asArr(documents.authorization || documents.authorizations),
             ...asArr(followUp.authorizations || followUp.authorization),
             ...asArr(followUp.generatedAuthorizations)].length +
            [...asArr(documents.referral || documents.referrals),
             ...asArr(followUp.referrals || followUp.referral),
             ...asArr(followUp.generatedReferrals)].length
          const detect = currentCase.detect || {}
          const uploadCount = (detect.photos?.length || 0) + (detect.documents?.length || 0) + (detect.videos?.length || 0)
          const agreementSigned = !!currentCase.patientAgreement?.signed
          const encounterCount = Array.isArray(currentCase.encounters) ? currentCase.encounters.length : 0

          // The header has to be true when the group is shut, so it counts only
          // what is actually WAITING ON THE PATIENT — not how many rows exist.
          const needsYou = (agreementSigned ? 0 : 1)
          const docsMeta = needsYou > 0
            ? t('caseHome.needsYou', { count: needsYou, defaultValue: '{{count}} needs you' })
            : uploadCount > 0
              ? `${uploadCount} ${uploadCount === 1 ? t('caseHome.file', 'file') : t('caseHome.files', 'files')}`
              : t('caseHome.nothingPending', 'Nothing pending')

          return (
            <>
              <CaseGroup icon={FolderOpen} name={t('caseHome.groupDocuments', 'Documents & records')} meta={docsMeta}>
                <GroupLine
                  first
                  icon={Upload}
                  title={t('caseHome.uploadRecords', 'Upload records')}
                  onClick={() => navigate('/case/detect')}
                  tag={uploadCount > 0 ? <Tag tone="info">{uploadCount} {uploadCount === 1 ? t('caseHome.file', 'file') : t('caseHome.files', 'files')}</Tag> : undefined}
                />
                <GroupLine
                  icon={FileSignature}
                  title={t('caseHome.careAgreement', 'Care agreement')}
                  onClick={agreementSigned ? () => navigate('/case/agreement') : undefined}
                  locked={!agreementSigned}
                  tag={agreementSigned
                    ? <Tag tone="ok">{t('caseHome.signed', 'Signed')}</Tag>
                    : <Tag tone="warn">{t('caseHome.pending', 'Pending')}</Tag>}
                />
                <GroupLine
                  icon={FileCheck}
                  title={t('caseHome.referralsAuths', 'Referrals & authorizations')}
                  onClick={() => navigate('/case/authorizations')}
                  tag={authRefCount > 0 ? <Tag tone="info">{authRefCount} {t('caseHome.open', 'open')}</Tag> : undefined}
                />
                {/* ★★ s101 P1.2 — THE CASE REPORT HAS A HOME. A direct-to-MD case has no share hub, so
                    the summary PDF had no door at all (Dr. Rami's memo; Alex Build 65 x_20). It opens the
                    SAME in-app viewer as the share hub and Diagnose — the Document Sharing Agreement first,
                    once per case (visitPrepAgreed), never the share sheet. Shown once Describe is done. */}
                <GroupLine
                  icon={FileText}
                  title={t('caseHome.caseReport', 'Your case report')}
                  onClick={describeComplete ? () => {
                    if (currentCase.visitPrepAgreed || currentCase.visit_prep_agreed) setReportViewerOpen(true)
                    else setReportAgreementOpen(true)
                  } : undefined}
                  locked={!describeComplete}
                  tag={describeComplete
                    ? <Tag tone="ok">{t('caseHome.ready', 'Ready')}</Tag>
                    : <Tag>{t('caseHome.afterSummary', 'After your summary')}</Tag>}
                />
                <GroupLine
                  icon={FileText}
                  title={t('caseHome.medicalReport', 'Medical report')}
                  onClick={isFinalized ? () => navigate('/case/note') : undefined}
                  locked={!isFinalized}
                  tag={isFinalized
                    ? <Tag tone="ok">{t('caseHome.ready', 'Ready')}</Tag>
                    : <Tag>{t('caseHome.afterClose', 'After close')}</Tag>}
                />
              </CaseGroup>

              <CaseGroup
                icon={Clock}
                name={t('caseHome.groupHistory', 'Case history & settings')}
                meta={encounterCount > 0
                  ? `${encounterCount} ${encounterCount === 1 ? t('caseHome.encounter', 'encounter') : t('caseHome.encounters', 'encounters')}`
                  : undefined}
              >
                <GroupLine
                  first
                  icon={Stethoscope}
                  title={t('caseHome.caseEncounters', 'Case encounters')}
                  sub={t('caseHome.fullTimeline', 'Full timeline of this case')}
                  onClick={() => navigate('/case/encounters', { state: { from: 'caseHome' } })}
                />
                <GroupLine
                  icon={FilePlus}
                  title={t('caseHome.customDocuments', 'Custom documents')}
                  sub={isFinalized ? t('caseHome.customDocsReady', 'Upload a form for your doctor to fill in and sign') : undefined}
                  onClick={isFinalized ? () => setShowDocHandler(true) : undefined}
                  locked={!isFinalized}
                  tag={isFinalized ? undefined : <Tag>{t('caseHome.afterFinalize', 'After finalize')}</Tag>}
                />
                {describeComplete && currentCase.status !== 'archived' && (
                  <GroupLine
                    icon={Archive}
                    iconBg="#FDECEC"
                    iconColor="#C0392B"
                    title={t('caseHome.archiveCase')}
                    onClick={() => setShowArchiveModal(true)}
                  />
                )}
                {!describeComplete && !isHeldByDoctor(currentCase) && (
                  <GroupLine
                    icon={Trash2}
                    iconBg="#FDECEC"
                    iconColor="#C0392B"
                    title={t('caseHome.deleteCase')}
                    onClick={() => setShowDeleteModal(true)}
                  />
                )}
              </CaseGroup>
            </>
          )
        })()}

      </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal onClose={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('caseHome.deleteCase')}</h3>
            <p className="text-gray-600 mb-6">{t('caseHome.deleteCaseConfirm')}</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  if (deleteCase) {
                    // ★ PROD-416: a refused delete leaves the case in the list, and says so.
                    deleteCase(currentCase.id).catch((err) => reportDeleteFailure(t, err))
                  } else {
                    clearCurrentCase()
                  }
                  navigate('/cases')
                }}
                className="w-full py-3 bg-red-500 text-white rounded-full font-semibold"
              >
                {t('caseHome.deleteCase')}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* s101 P1.2 — the Case Report from Documents & records */}
      <CaseReportViewer open={reportViewerOpen} caseObj={currentCase} onClose={() => setReportViewerOpen(false)} t={t} />
      {reportAgreementOpen && (
        <OverlayPortal>
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(11,22,32,.45)' }}>
          <div className="w-full max-h-[88%] overflow-y-auto bg-gray-50 rounded-t-3xl p-4 bottom-actions">
            <div className="flex justify-end mb-1">
              <button aria-label={t('common.close', 'Close')} onClick={() => setReportAgreementOpen(false)} className="p-1"><X size={20} style={{ color: '#15354F' }} /></button>
            </div>
            <VisitPrepDisclaimer
              onAgree={async () => {
                const at = new Date().toISOString()
                try {
                  await updateAndSaveCase({ visitPrepAgreed: true, visitPrepAgreedAt: at })
                  const { casesAPI } = await import('../services/api')
                  await casesAPI.update(currentCase.id, { visit_prep_agreed: true, visit_prep_agreed_at: at })
                } catch (err) { console.warn('[CaseHome] report agreement sync failed:', err?.message) }
                setReportAgreementOpen(false)
                setReportViewerOpen(true)
              }}
              onCancel={() => setReportAgreementOpen(false)}
            />
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* Archive Modal */}
      {showRemindSheet && (
        <RemindDoctorSheet caseId={currentCase.id} onClose={() => setShowRemindSheet(false)} />
      )}

      {showDocHandler && (
        <CustomDocHandler
          caseId={currentCase.id}
          doctor={currentCase.doctor}
          onClose={() => setShowDocHandler(false)}
        />
      )}

      {showArchiveModal && (
        <Modal onClose={() => setShowArchiveModal(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Archive className="text-gray-600" size={24} />
              </div>
              <button onClick={() => setShowArchiveModal(false)} className="text-gray-400">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('caseHome.archiveCaseTitle')}</h3>
            <p className="text-gray-600 mb-4">{t('caseHome.archiveCaseDesc')}</p>
            
            {/* Archive Reasons */}
            <div className="space-y-2 mb-4">
              <button
                onClick={() => setArchiveReason('resolved')}
                className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                  archiveReason === 'resolved' 
                    ? 'border-orella-500 bg-orella-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-gray-700">{t('caseHome.archiveReasonResolved')}</span>
              </button>
              <button
                onClick={() => setArchiveReason('treatment')}
                className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                  archiveReason === 'treatment' 
                    ? 'border-orella-500 bg-orella-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-gray-700">{t('caseHome.archiveReasonTreatment')}</span>
              </button>
              <button
                onClick={() => setArchiveReason('other')}
                className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                  archiveReason === 'other' 
                    ? 'border-orella-500 bg-orella-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-gray-700">{t('caseHome.archiveReasonOther')}</span>
              </button>
              
              {archiveReason === 'other' && (
                <input
                  type="text"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder={t('caseHome.archiveReasonPlaceholder')}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:border-orella-500 focus:outline-none"
                />
              )}
            </div>
            
            {/* Reminder about uploading notes */}
            <div className="bg-blue-50 rounded-xl p-3 mb-4">
              <p className="text-sm text-blue-700">
                💡 {t('caseHome.caseArchivedDesc')}
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const reason = archiveReason === 'other' ? otherReason : archiveReason
                  if (archiveCase) {
                    archiveCase(currentCase.id, reason)
                  }
                  setShowArchiveModal(false)
                  navigate('/cases')
                }}
                disabled={!archiveReason || (archiveReason === 'other' && !otherReason)}
                className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t('caseHome.archiveAndClose')}
              </button>
              <button
                onClick={() => setShowArchiveModal(false)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* v125.2.1: Cancel observation bridge confirmation modal */}
      {showCancelBridge && (
        <Modal onClose={() => setShowCancelBridge(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <button onClick={() => setShowCancelBridge(false)} className="text-gray-400 bg-transparent border-none cursor-pointer">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('caseHome.cancelFollowUp')}</h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('caseHome.cancelFollowUpBody', 'This will cancel your pending follow-up request and return you to the observation period. You can report a new finding and request a follow-up again at any time.')}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleCancelBridge}
                disabled={cancellingBridge}
                className="w-full py-3 border border-red-300 text-red-500 rounded-full font-semibold disabled:opacity-50 cursor-pointer bg-transparent"
              >
                {cancellingBridge ? t('caseHome.cancelling') : t('caseHome.yesCancelRequest')}
              </button>
              <button
                onClick={() => setShowCancelBridge(false)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold cursor-pointer border-none"
              >
                {t('caseHome.keepRequest')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* v138.3.0: Reschedule Appointment Modal */}
      {showRescheduleModal && (
        <Modal onClose={() => { if (!rescheduleSubmitting) setShowRescheduleModal(false) }}>
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden" style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <RefreshCw size={16} color="#46A8F9" />
                  <span className="text-[15px] font-bold" style={{ color: NAVY }}>{t('caseHome.rescheduleAppt')}</span>
                </div>
                <button onClick={() => { if (!rescheduleSubmitting) setShowRescheduleModal(false) }}
                  className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center border-none cursor-pointer">
                  <X size={14} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-3" style={{ maxHeight: '55vh' }}>
              {/* Success state */}
              {rescheduleComplete && (
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: '#D5EFD3' }}>
                    <Check size={24} color="#22C55E" />
                  </div>
                  <p className="text-[15px] font-bold mb-1" style={{ color: NAVY }}>{t('caseHome.rescheduled')}</p>
                  <p className="text-[12px] text-gray-500 mb-1">
                    {formatCalendarDay(rescheduleDate, { weekday: 'long', month: 'long', day: 'numeric' }, 'en-US') || rescheduleDate} at {(() => { try { const [h,m] = rescheduleTime.split(':').map(Number); return `${h > 12 ? h-12 : h || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}` } catch { return rescheduleTime } })()}
                  </p>
                  <SlotPlaceLine place={slotPlace(reschedulePlaces, rescheduleDate, rescheduleTime, rescheduleVisitType === 'video')} className="justify-center mb-1" />
                  <p className="text-[11px] text-gray-400 mb-4">{t('caseHome.doctorNotified')}</p>
                  <button onClick={() => setShowRescheduleModal(false)}
                    className="px-6 py-2.5 rounded-xl text-[13px] font-bold text-white border-none cursor-pointer"
                    style={{ background: `linear-gradient(135deg, ${NAVY}, #1E4A6E)` }}>
                    {t('common.done', 'Done')}
                  </button>
                </div>
              )}

              {/* Error state */}
              {rescheduleError && !rescheduleComplete && (
                <div className="text-center py-4">
                  <p className="text-[12px] text-red-500 mb-3">{rescheduleError}</p>
                  {/* Round 2 (A4): after a FAILED READ, "Try again" reads the calendar again.
                      Only clearing the error used to drop the patient onto the empty state,
                      which then said "no slots online, contact the office". */}
                  <button onClick={() => (rescheduleNoSlotsReason === 'fetch-failed' ? openRescheduleModal(rescheduleType) : setRescheduleError(null))} className="text-[12px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: ORELLA_BLUE }}>
                    {t('caseHome.tryAgain', 'Try again')}
                  </button>
                </div>
              )}

              {/* Loading slots */}
              {rescheduleSlotsLoading && !rescheduleComplete && (
                <div className="text-center py-8">
                  <OrellaSpinner size={20} tone="inherit" className="mx-auto mb-2 text-gray-300" />
                  <p className="text-[12px] text-gray-400">{t('caseHome.loadingTimes')}</p>
                </div>
              )}

              {/* No slots — and WHICH no. See openRescheduleModal for the four causes. */}
              {!rescheduleSlotsLoading && rescheduleSlots !== null && rescheduleSlots.length === 0 && !rescheduleComplete && !rescheduleError && (
                <div className="text-center py-6" data-reschedule-empty={rescheduleNoSlotsReason || 'unknown'}>
                  <Calendar size={24} className="mx-auto mb-2 text-gray-300" />
                  {rescheduleNoSlotsReason === 'no-doctor' ? (
                    <>
                      <p className="text-[13px] font-semibold text-gray-500 mb-1">{t('caseHome.noDoctorToReschedule', 'No doctor on this case yet')}</p>
                      <p className="text-[11px] text-gray-400">{t('caseHome.noDoctorToRescheduleSub', 'Once a doctor picks up your case you can change the time here.')}</p>
                    </>
                  ) : rescheduleNoSlotsReason === 'fetch-failed' ? (
                    <>
                      <p className="text-[13px] font-semibold text-gray-500 mb-1">{t('caseHome.slotsFetchFailed', "We couldn't load available times just now.")}</p>
                      <button onClick={() => openRescheduleModal(rescheduleType)} className="text-[12px] font-semibold border-none bg-transparent cursor-pointer" style={{ color: ORELLA_BLUE }}>
                        {t('caseHome.tryAgain', 'Try again')}
                      </button>
                    </>
                  ) : rescheduleNoSlotsReason === 'none-open' ? (
                    <>
                      <p className="text-[13px] font-semibold text-gray-500 mb-1">{t('caseHome.noOpenTimes', 'No open times in the next 30 days')}</p>
                      <p className="text-[11px] text-gray-400">{t('caseHome.contactOffice')}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[13px] font-semibold text-gray-500 mb-1">{t('caseHome.noSlotsAvailable')}</p>
                      <p className="text-[11px] text-gray-400">{t('caseHome.contactOffice')}</p>
                    </>
                  )}
                </div>
              )}

              {/* Date selection */}
              {!rescheduleSlotsLoading && rescheduleSlots?.length > 0 && !rescheduleDate && !rescheduleComplete && !rescheduleError && (
                <>
                  <p className="text-[12px] font-semibold mb-2" style={{ color: NAVY }}>{t('caseHome.selectNewDate')}</p>
                  <div className="space-y-1.5">
                    {rescheduleSlots.map(slot => {
                      return (
                        <button key={slot.date} onClick={() => setRescheduleDate(slot.date)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 bg-white text-left cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-all">
                          <div className="w-9 h-9 rounded-lg flex flex-col items-center justify-center flex-shrink-0" style={{ background: '#F1F5F9' }}>
                            <span className="text-[8px] font-semibold text-gray-400 leading-none">{formatCalendarDay(slot.date, { month: 'short' }, 'en-US')}</span>
                            <span className="text-[14px] font-bold leading-none" style={{ color: NAVY }}>{formatCalendarDay(slot.date, { day: 'numeric' }, 'en-US')}</span>
                          </div>
                          <div className="flex-1">
                            <span className="text-[12px] font-semibold block" style={{ color: NAVY }}>{slot.dayName}</span>
                            <span className="text-[10px] text-gray-400">{slot.times.length} slot{slot.times.length !== 1 ? 's' : ''}</span>
                          </div>
                          <ChevronRight size={14} className="text-gray-300" />
                        </button>
                      )
                    })}
                  </div>
                </>
              )}

              {/* Time selection */}
              {rescheduleDate && !rescheduleComplete && !rescheduleError && (() => {
                const selectedSlot = rescheduleSlots?.find(s => s.date === rescheduleDate)
                if (!selectedSlot) return null
                // ★ D7 (PD7-2): the location name under each time (more than one location) and
                //   the picked time's place in the preview; nothing for a virtual visit or an
                //   API without `locations`.
                const rescheduleVirtual = rescheduleVisitType === 'video'
                const timePlaceNames = slotPlaceNames(reschedulePlaces, rescheduleDate, selectedSlot.times, rescheduleVirtual) || {}
                return (
                  <>
                    <button onClick={() => { setRescheduleDate(null); setRescheduleTime(null) }}
                      className="flex items-center gap-1 text-[11px] font-semibold border-none bg-transparent cursor-pointer mb-2 p-0" style={{ color: ORELLA_BLUE }}>
                      <ChevronLeft size={13} /> {t('caseHome.changeDate', 'Change date')}
                    </button>
                    <p className="text-[13px] font-bold mb-0.5" style={{ color: NAVY }}>
                      {formatCalendarDay(rescheduleDate, { weekday: 'long', month: 'long', day: 'numeric' }, 'en-US') || rescheduleDate}
                    </p>
                    <p className="text-[11px] text-gray-400 mb-3">{t('caseHome.selectTime')}</p>
                    <div className="grid grid-cols-3 gap-1.5 mb-4">
                      {selectedSlot.times.map(time => {
                        const isSelected = rescheduleTime === time
                        const [h, m] = time.split(':').map(Number)
                        const label = `${h > 12 ? h-12 : h || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`
                        return (
                          <button key={time} onClick={() => setRescheduleTime(time)}
                            className="py-2 rounded-lg text-[12px] font-semibold border cursor-pointer transition-all"
                            style={{
                              borderColor: isSelected ? ORELLA_BLUE : '#E2E8F0',
                              background: isSelected ? ORELLA_BLUE : '#fff',
                              color: isSelected ? '#fff' : NAVY,
                              borderWidth: isSelected ? 2 : 1.5,
                            }}>
                            {label}
                            {timePlaceNames[time] ? (
                              <span className="block text-[10px] font-medium leading-tight mt-0.5 truncate px-1" data-slot-place-name="">{timePlaceNames[time]}</span>
                            ) : null}
                          </button>
                        )
                      })}
                    </div>
                    {/* Visit type */}
                    <p className="text-[11px] font-semibold mb-1.5" style={{ color: NAVY }}>{t('caseHome.visitType')}</p>
                    <div className="flex gap-2 mb-4">
                      {[{ id: 'in-person', label: t('caseHome.inperson') }, { id: 'video', label: t('caseHome.virtual') }].filter(({ id }) => rescheduleOfferedFor(rescheduleType).includes(id === 'video' ? 'video' : 'in_person')).map(({ id, label }) => (
                        <button key={id} onClick={() => setRescheduleVisitType(id)}
                          className="flex-1 py-2 rounded-lg text-[12px] font-semibold border cursor-pointer transition-all"
                          style={{
                            borderColor: rescheduleVisitType === id ? ORELLA_BLUE : '#E2E8F0',
                            background: rescheduleVisitType === id ? '#E9F8FF' : '#fff',
                            color: rescheduleVisitType === id ? ORELLA_BLUE : '#64748B',
                            borderWidth: rescheduleVisitType === id ? 2 : 1.5,
                          }}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {/* Preview + confirm */}
                    {rescheduleTime && (
                      <div className="p-3 rounded-xl mb-3" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                        <p className="text-[11px] font-semibold mb-0.5" style={{ color: '#166534' }}>{t('caseHome.newAppointment')}</p>
                        <p className="text-[13px] font-bold" style={{ color: NAVY }}>
                          {formatCalendarDay(rescheduleDate, { weekday: 'long', month: 'long', day: 'numeric' }, 'en-US') || rescheduleDate} at {(() => { const [h,m] = rescheduleTime.split(':').map(Number); return `${h > 12 ? h-12 : h || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}` })()}
                        </p>
                        <p className="text-[11px] text-gray-500">{rescheduleVisitType === 'video' ? t('caseHome.virtualVisit') : 'In-Person Visit'}</p>
                        <SlotPlaceLine place={slotPlace(reschedulePlaces, rescheduleDate, rescheduleTime, rescheduleVirtual)} className="mt-1" />
                      </div>
                    )}
                    <button onClick={handleRescheduleSubmit} disabled={!rescheduleTime || rescheduleSubmitting}
                      className="w-full py-3 rounded-xl text-[14px] font-bold text-white border-none cursor-pointer"
                      style={{
                        background: rescheduleTime ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
                        color: rescheduleTime ? '#fff' : '#94A3B8',
                        opacity: rescheduleSubmitting ? 0.7 : 1,
                      }}>
                      {rescheduleSubmitting ? t('caseHome.rescheduling') : t('caseHome.confirmReschedule')}
                    </button>
                  </>
                )
              })()}
            </div>
          </div>
        </Modal>
      )}

      {consentSheet}

      {/* ★ Same copy, same confirm/cancel labels and same red as the Diagnose
          screen's — one destructive action should not read as two different
          ones depending on which screen the patient started from.
          PROD-285: and, like the Diagnose screen's, the message says that
          withdrawing also stops a live share code. */}
      <ConfirmModal
        open={showWithdrawModal}
        title={t('diagnose.withdrawCase')}
        message={(currentCase?.shareCode || currentCase?.share_code) ? t('shareCodeKill.withdrawBody') : t('diagnose.cancelWarning')}
        confirmLabel={t('diagnose.withdrawCaseLabel')}
        cancelLabel={t('diagnose.keepAppointmentLabel')}
        confirmColor="#EF4444"
        loading={isWithdrawing}
        icon="warning"
        onConfirm={handleWithdrawCase}
        onCancel={() => { if (!isWithdrawing) setShowWithdrawModal(false) }}
      />

    </div>
  )
}

// v121.7.2: Error boundary to catch render crashes after phase transitions
// (e.g. encounter acknowledge → navigate back causes brief stale-state render)
class CaseHomeErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(error, info) {
    console.error('[CaseHome] Render crash caught by boundary:', error, info)
  }
  render() {
    // ★★★ v242.184.0 — THIS FALLBACK CRASHED, AND IT MASKED THE ERROR IT WAS CATCHING.
    //   v242.182.0 put its two sentences through t(). THIS IS A CLASS COMPONENT: hooks
    //   cannot run in it, there was no `t` in scope, and the fallback threw
    //   ReferenceError the moment it was asked to render. React had already called
    //   getDerivedStateFromError, so the ORIGINAL error was discarded and the app-level
    //   boundary reported this one instead — componentDidCatch below never even ran,
    //   because a render that throws never commits.
    // ★★★ A CRASH SCREEN MUST NOT BE ABLE TO CRASH. `t` arrives as a prop from the
    //   function wrapper, and this line means a missing or broken one costs the
    //   translation and nothing else. The i18n context is exactly what may have thrown.
    const tt = typeof this.props.t === 'function' ? this.props.t : (_k, d) => d
    if (this.state.hasError) {
      return (
        <div className="screen-min-h flex flex-col items-center justify-center bg-gray-50 px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#46A8F9" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-[#15354F] mb-2">{tt('caseHome.somethingWentWrong', 'Something went wrong')}</h2>
          <p className="text-sm text-gray-500 mb-6">{tt('caseHome.caseDataIsSafe', 'Your case data is safe. Tap reload to try again.')}</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload() }}
            className="px-6 py-3 rounded-full font-semibold text-white text-sm"
            style={{ backgroundColor: '#46A8F9' }}
          >
            {tt('caseHome.reload', 'Reload')}
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const CaseHomeWithBoundary = (props) => {
  const { t } = useI18n()
  return (
  <CaseHomeErrorBoundary t={t}>
    <CaseHome {...props} />
  </CaseHomeErrorBoundary>
  )
}

export { CaseHomeWithBoundary as default }
